import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { stateLabel } from '../../../lib/guestRecovery.mjs';
import { emptyState } from '../shared/components/Narrative';
import { icon } from '../shared/components/icons';
import { PmsSendConfirm } from '../shared/components/PmsSendConfirm';   // R115 guest: the confirm step, shared with the Calendar's Message action

// R112 — Messaging: the universal inbox (Jesse: "Let's totally redesign the
// Guest Text, and rename that tab Messaging … you can also see the PMS
// messages as well. Can we build a universal inbox?"). The R52–R111 Guest
// Texts page (Quo / OpenPhone SMS) evolved IN PLACE — same class, same
// PageName 'guest-texts', same sessionStorage keys ('qx_focus_phone' from the
// Calendar, 'qx_line'), same data-qx-* handlers and quiet patch paths — now
// fed by /api/messaging, which merges the Quo SMS threads with the Hostaway
// (PMS: Airbnb / VRBO / Booking / email) conversations by guest phone.
// Layout per HostBuddy's inbox (no hero images — initials avatars): a list
// with guest · property · dates · status pill · channel chips, a thread with
// PMS · SMS · Issue · Notes tabs and a compose locked to the active channel,
// and a Reservation details panel on the right. A PMS reply goes through an
// explicit confirm modal and the server's MESSAGING_PMS_SEND flag. No
// auto-replies, no AI on the send path — a human reads, types and sends.
const API = '/api/messaging';
const QUO = '/api/quo';   // actions / dismiss / restore / find / contacts / lines / diag / conversation-done keep their R111 contracts
// AbortSignal.timeout is missing on Safari ≤ 15 — fall back to no timeout rather than throwing before fetch
const timeoutSignal = (ms: number): AbortSignal | undefined => (typeof AbortSignal !== 'undefined' && typeof (AbortSignal as any).timeout === 'function') ? (AbortSignal as any).timeout(ms) : undefined;
const REFRESH_MS = 30000;   // auto-refresh while open — new messages surface without the webhook
const INBOX_TIMEOUT_MS = 45000;   // R111 B4: a hung /inbox turns into the Retry state instead of a spinner forever
const THREAD_TIMEOUT_MS = 30000;
const STALE_FOLLOWUP_MS = 2500;   // R111 A4: the server answered stale:true and is rebuilding — re-ask shortly
const SMS_MAX = 1600, PMS_MAX = 2000;
const TZ = 'America/Los_Angeles';   // message stamps render in PT — the portfolio's timezone, not the viewer's
type Tab = 'sms' | 'pms' | 'issue' | 'notes';
type Channel = 'all' | 'sms' | 'pms';

export class QuoInboxPage {
  private container: HTMLElement;
  private configured: { sms: boolean; pms: boolean } | null = null;
  private pmsSend = false;               // server flag MESSAGING_PMS_SEND — PMS compose is read-only until it is on
  private rows: any[] = [];              // unified rows (URow) from /api/messaging/inbox
  private counts: any = {};
  private needsReply = 0;
  private loading = true;
  private loadError = false;
  private selected: any = null;          // the open row
  private tab: Tab = 'sms';              // the thread tab = the channel the compose is locked to
  private threadData: Record<string, any> = {};        // sms / pms thread payloads (undefined = not loaded)
  private threadErr: Record<string, boolean> = {};     // fetch FAILED ≠ genuinely empty
  private threadBusy: Record<string, boolean> = {};
  private notes: any[] | null = null;    // the Notes tab (null = not loaded)
  private noteDraft = '';
  private noteSaving = false;
  private actions: any[] = [];           // R54: open action items (manual + auto)
  private draft = '';                    // reply text — class-backed so redraws don't lose it
  private markDone = false;              // "mark done after sending" toggle (SMS only)
  private sending = false;
  private note = '';
  // R112: the PMS send confirm modal — mounted on document.body, never inside #qx-body (opening it must not redraw the thread).
  // R115 guest: lifted into shared/components/PmsSendConfirm (the Calendar's Message action uses the same step).
  private confirm = new PmsSendConfirm();
  private get confirmOpen(): boolean { return this.confirm.isOpen; }
  private timer: any = null;
  private dismissing: string | null = null;
  private showQuiet = false;
  private showDismissed = false;
  private bodyEl: HTMLElement | null = null;   // captured #qx-body — poll self-terminates when it detaches
  private pendingScroll = false;
  private detailsOpen = false;           // ≤1100px: the Reservation details drawer
  // filters: channel + window are server-side (they change the fetch); the chips + search narrow client-side
  private channel: Channel = 'all';
  private window = 14;
  private q = '';
  private chip: '' | 'needs' | 'inhouse' | 'arriving' | 'negative' | 'team' = '';
  // R57 New-text composer (unchanged)
  private composing = false;
  private composeTo = '';
  private composeLine = '';
  private composeMsg = '';
  private composeNote = '';
  private composeSending = false;
  private contacts: any[] | null = null;
  private lines: any[] | null = null;
  private perLine: number | null = null;
  private lineNeed = new Map<string, number>();
  private fetchErrors: { line: string; error: string }[] = [];
  private errors: { channel: string; error: string }[] = [];
  private lineFilter: string = (() => { try { return sessionStorage.getItem('qx_line') || ''; } catch { return ''; } })();
  private builtAt: string | null = null;
  private stale = false;
  private staleFollowups = 0;
  private refreshError = false;
  private stampTimer: any = null;
  private focusNote = '';

  constructor(container: HTMLElement) { this.container = container; }
  private esc(s: any): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }

  async render(): Promise<void> {
    this.closeConfirm();   // a modal left open on the last visit never survives a re-render
    this.container.innerHTML = `<div class="page-content"><div id="qx-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div></div>`;
    this.bodyEl = this.container.querySelector('#qx-body');
    // R111 B1: persistent instance — on a re-visit paint what we already hold
    // INSTANTLY and refresh quietly behind it. The spinner is for a cold page.
    const warm = !!this.configured && (this.configured.sms || this.configured.pms) && !this.loadError && this.rows.length > 0;
    if (warm) { this.loading = false; this.pendingScroll = !!this.selected; this.draw(); }
    const focusPhone = this.takeFocusPhone();
    const focusConv = focusPhone ? null : this.takeFocusConv();   // R115 guest
    if (warm && focusPhone && this.openFromMemory(focusPhone)) {
      this.startPolling();
      void this.loadInbox(true);
      return;
    }
    if (warm && focusConv && this.openConvFromMemory(focusConv.cid)) {
      this.startPolling();
      void this.loadInbox(true);
      return;
    }
    await this.loadInbox(warm);
    this.startPolling();
    if (focusPhone) await this.focusFromCalendar(focusPhone);
    else if (focusConv) this.focusConversation(focusConv);
  }

  // R115 guest: the Calendar's Message action hands over a PMS CONVERSATION
  // (one-shot sessionStorage 'qx_focus_conv' = {cid, rid}) when the booking
  // has no guest phone — most Airbnb bookings mask it, so the phone hand-off
  // ('qx_focus_phone') could never open their thread. Ids only: no message
  // text, no phone ever goes into storage.
  private takeFocusConv(): { cid: string; rid: string | null } | null {
    let raw = '';
    try { raw = sessionStorage.getItem('qx_focus_conv') || ''; if (raw) sessionStorage.removeItem('qx_focus_conv'); } catch { return null; }
    if (!raw) return null;
    try {
      const v = JSON.parse(raw);
      const cid = String((v && v.cid) || '');
      const rid = v && v.rid != null ? String(v.rid) : '';
      if (!/^[\w.-]{1,64}$/.test(cid)) return null;
      return { cid, rid: /^[\w-]{1,40}$/.test(rid) ? rid : null };
    } catch { return null; }
  }
  private openConvFromMemory(cid: string): boolean {
    const row = this.rows.find(r => r.pms && String(r.pms.conversation_id) === cid);
    if (!row) return false;
    void this.openThread(row, 'pms');
    return true;
  }
  private focusConversation(f: { cid: string; rid: string | null }): void {
    if (this.openConvFromMemory(f.cid)) return;
    if (!this.configured || !this.configured.pms) {
      this.focusNote = this.configured ? "Hostaway isn't connected here, so that booking's PMS thread can't open." : "Couldn't open the conversation — the inbox didn't load. Retry, then use Message on the Calendar again.";
      this.draw();
      return;
    }
    // older than the inbox window: open it from a synthetic row (the thread
    // route fills the stay / guest / property from the reservation id)
    void this.openThread({
      id: `pms:${f.cid}`, channels: ['pms'],
      guest: { name: '', phone: null, email: null, contact_kind: 'guest' },
      property: { listing_id: '', name: '' },
      reservation: f.rid ? { hostaway_id: f.rid, check_in: '', check_out: '', nights: null, guests: null, status: null, channel_name: null, confirmation_code: null, phase: null, hostaway_url: null } : null,
      last_message: null, last_activity_at: null, needs_reply: false, last_trusted: true, dismissed: false,
      sms: null,
      pms: { conversation_id: f.cid, listing_map_id: '', reservation_id: f.rid, needs_reply: false, last_trusted: true, last_message: null, last_activity_at: null },
    }, 'pms');
  }

  // R110 #1: the Calendar's "Open conversation" hands over a phone number in a
  // one-shot sessionStorage key.
  private takeFocusPhone(): string {
    let raw = '';
    try { raw = sessionStorage.getItem('qx_focus_phone') || ''; if (raw) sessionStorage.removeItem('qx_focus_phone'); } catch { return ''; }
    return this.normPhone(raw);
  }
  // R112: a PMS-only row matches by the reservation's guest phone too
  private findRow(phone: string): any {
    // R116 #2: the number's OWN thread first — a vendor group it sits in is never "their conversation"
    const own = this.rows.find(r => !this.isGroup(r) && ((r.sms && ((r.sms.participants || []).some((p: string) => this.normPhone(p) === phone) || this.normPhone(r.sms.display_number || '') === phone)) || this.normPhone((r.guest && r.guest.phone) || '') === phone));
    return own || this.rows.find(r => r.sms && (r.sms.participants || []).some((p: string) => this.normPhone(p) === phone));
  }
  // R116 #2: a Quo GROUP thread (a vendor group text: vendor + team + typed numbers)
  private isGroup(r: any): boolean { return !!(r && (r.group || (r.sms && r.sms.group) || (r.guest && r.guest.contact_kind === 'group'))); }
  private membersOf(r: any): any[] { return Array.isArray(r && r.members) ? r.members : (r && r.sms && Array.isArray(r.sms.members) ? r.sms.members : []); }
  private memberName(m: any): string { return String((m && m.name) || '').trim() || this.fmtPhoneUS(m && m.phone) || 'Unknown number'; }
  private fmtPhoneUS(p: any): string {
    const n = this.normPhone(String(p || ''));
    const mm = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(n);
    return mm ? `(${mm[1]}) ${mm[2]}-${mm[3]}` : n;
  }
  private static readonly MEMBER_KIND: Record<string, string> = { vendor: 'vendor', staff: 'team', cleaner: 'cleaner', owner: 'owner', guest: 'guest' };
  private openFromMemory(phone: string): boolean {
    const row = this.findRow(phone);
    if (!row) return false;
    if (row.sms && this.lineFilter && String(row.sms.phone_number_id || '') !== this.lineFilter) this.setLineFilter(String(row.sms.phone_number_id || ''));
    void this.openThread(row);
    return true;
  }
  private setLineFilter(id: string): void {
    this.lineFilter = id;
    try { if (id) sessionStorage.setItem('qx_line', id); else sessionStorage.removeItem('qx_line'); } catch { /* private mode */ }
    if (id && !this.composing) this.composeLine = id;
  }

  // Open that guest's thread if the loaded page has it (SMS or PMS). Otherwise
  // ask Quo where the thread lives (GET /api/quo/find) and open it from a
  // synthetic row; the composer is the fallback only when NO thread exists.
  private async focusFromCalendar(phone: string): Promise<void> {
    if (!phone) return;
    const anyOn = !!this.configured && (this.configured.sms || this.configured.pms);
    if (!anyOn) {
      this.focusNote = this.configured
        ? `Messaging isn't connected, so the conversation with ${phone} can't open here.`
        : `Couldn't open the conversation with ${phone} — the inbox didn't load. Retry, then use "Open conversation" on the Calendar again.`;
      this.draw();
      return;
    }
    if (this.openFromMemory(phone)) return;
    if (this.configured && this.configured.sms) {
      const j = await fetch(`${QUO}/find?phone=${encodeURIComponent(phone)}`, { signal: timeoutSignal(THREAD_TIMEOUT_MS) }).then(r => r.ok ? r.json() : null).catch(() => null);
      const f = j && j.found;
      if (f && f.phone_number_id) {
        const lineId = String(f.phone_number_id);
        if (this.lineFilter && this.lineFilter !== lineId) { this.setLineFilter(lineId); this.patchList(); void this.loadInbox(true); }
        const parts = Array.isArray(f.participants) && f.participants.length ? f.participants.map(String) : [phone];
        void this.openThread({
          id: `sms:${f.conversation_id || 'new'}`, channels: ['sms'],
          guest: { name: f.guest_name || '', phone: parts[0] || phone, email: null, contact_kind: f.contact_kind || null },
          property: { listing_id: '', name: f.listing || '' }, reservation: null,
          last_message: null, last_activity_at: f.last_activity_at || null, needs_reply: false, dismissed: false,
          sms: { conversation_id: f.conversation_id || null, phone_number_id: lineId, participants: parts, line: f.line || '', display_number: parts[0] || phone, needs_reply: false, last_message: null },
          pms: null,
        });
        return;
      }
    }
    if (this.configured && this.configured.sms) { this.composeTo = phone; void this.openCompose(); return; }
    this.focusNote = `No SMS line is connected, and ${phone} has no PMS thread in the current window.`;
    this.draw();
  }

  // App's 15-min auto-refresh asks first: veto it whenever the user is reading
  // a thread, has a draft, is composing or is mid-confirm (the R37 data-loss class).
  suspendAutoRefresh(): boolean { return !!(this.selected || this.draft.trim() || this.composing || this.confirmOpen || this.noteDraft.trim()); }
  private anyConfigured(): boolean { return !!this.configured && (this.configured.sms || this.configured.pms); }
  private canQuietRefresh(): boolean { return this.anyConfigured() && !this.sending && !this.draft.trim() && !this.composing && !this.confirmOpen; }
  private startPolling(): void {
    this.stopPolling();
    this.timer = setInterval(() => {
      if (!this.bodyEl || !this.bodyEl.isConnected) { this.stopPolling(); this.closeConfirm(); return; }
      if (this.canQuietRefresh()) void this.loadInbox(true);
    }, REFRESH_MS);
    this.stampTimer = setInterval(() => { if (!this.bodyEl || !this.bodyEl.isConnected) { this.stopPolling(); this.closeConfirm(); return; } this.patchStamp(); }, 10000);
  }
  private stopPolling(): void {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
    if (this.stampTimer) { clearInterval(this.stampTimer); this.stampTimer = null; }
  }

  private inboxUrl(): string {
    const p = new URLSearchParams();
    p.set('channel', this.channel); p.set('window', String(this.window));
    if (this.lineFilter) p.set('line', this.lineFilter);
    return `${API}/inbox?${p.toString()}`;
  }
  private async loadInbox(quiet = false): Promise<void> {
    if (!quiet) { this.loading = true; this.draw(); }
    const wantLine = this.lineFilter, wantCh = this.channel, wantWin = this.window;
    const j = await fetch(this.inboxUrl(), { signal: timeoutSignal(INBOX_TIMEOUT_MS) }).then(r => r.ok ? r.json() : null).catch(() => null);
    if (wantLine !== this.lineFilter || wantCh !== this.channel || wantWin !== this.window) return;   // the filter changed mid-flight — drop the stale answer
    this.loading = false;
    if (!j) {
      if (quiet && this.rows.length && this.anyConfigured()) { this.refreshError = true; this.patchStamp(); return; }
      this.loadError = true; this.draw(); return;
    }
    this.loadError = false;
    this.refreshError = false;
    this.configured = { sms: !!(j.configured && j.configured.sms), pms: !!(j.configured && j.configured.pms) };
    this.pmsSend = !!j.pms_send;
    this.rows = Array.isArray(j.rows) ? j.rows : [];
    this.counts = j.counts || {};
    this.builtAt = j.built_at || this.builtAt || (this.anyConfigured() ? new Date().toISOString() : null);
    this.stale = !!j.stale;
    if (j.stale && this.staleFollowups < 3) {
      this.staleFollowups++;
      setTimeout(() => { if (this.bodyEl && this.bodyEl.isConnected && this.canQuietRefresh()) void this.loadInbox(true); }, STALE_FOLLOWUP_MS);
    } else if (!j.stale) this.staleFollowups = 0;
    this.needsReply = j.needs_reply || 0;
    this.actions = j.actions || [];
    this.errors = Array.isArray(j.errors) ? j.errors : [];
    if (Array.isArray(j.lines) && j.lines.length) this.lines = j.lines;
    const smsRows = this.rows.filter(r => r.sms);
    if (!j.line) {
      this.lineNeed.clear();
      for (const r of smsRows) { const id = String(r.sms.phone_number_id || ''); if (id && !r.dismissed && r.sms.needs_reply) this.lineNeed.set(id, (this.lineNeed.get(id) || 0) + 1); }
    } else {
      this.lineNeed.set(String(j.line), smsRows.filter(r => !r.dismissed && r.sms.needs_reply).length);
    }
    if (this.lineFilter && this.lines && !this.lines.some((l: any) => String(l.id) === this.lineFilter)) { this.lineFilter = ''; try { sessionStorage.removeItem('qx_line'); } catch { /* private mode */ } }
    this.perLine = j.per_line || null;
    this.fetchErrors = Array.isArray(j.fetch_errors) ? j.fetch_errors : [];
    // Keep the selected row fresh (needs_reply / snippet / ticket). A row's id
    // can change between loads (an SMS thread gains a PMS twin → 'guest:…'),
    // so fall back to the SMS / PMS conversation ids.
    if (this.selected) {
      const s = this.selected;
      const upd = this.rows.find(r => r.id === s.id)
        || (s.sms && s.sms.conversation_id ? this.rows.find(r => r.sms && r.sms.conversation_id === s.sms.conversation_id) : null)
        || (s.pms && s.pms.conversation_id ? this.rows.find(r => r.pms && r.pms.conversation_id === s.pms.conversation_id) : null);
      if (upd) this.selected = upd;
    }
    // A quiet refresh NEVER rebuilds the body — thread open or not (R112
    // review [15]): it patches the list, the KPIs, the toolbar (skipped while
    // it holds the focus: the search box mid-word, the PMS window menu), the
    // action strip, the details panel and the stamp. draw() recreated the
    // search input and dropped its caret and dropdown every time the 30 s
    // poll or a stale follow-up landed with no thread open; and it never
    // touches the thread panel, so scroll / focus / caret / draft survive.
    if (quiet && this.container.querySelector('#qx-list')) { this.patchList(); this.patchKpis(); this.patchToolbar(); this.patchActions(); this.patchSide(); this.patchStamp(); }
    else this.draw();
  }

  private stampHtml(): string {
    if (!this.anyConfigured()) return '';
    const age = this.builtAt ? Math.max(0, Math.round((Date.now() - new Date(this.builtAt).getTime()) / 1000)) : null;
    const ago = age == null ? '' : age < 60 ? `${age}s ago` : age < 3600 ? `${Math.floor(age / 60)}m ago` : `${Math.floor(age / 3600)}h ago`;
    const note = this.refreshError ? ' · couldn\'t refresh' : this.stale ? ' · refreshing…' : '';
    return `<span class="qx-stamp ${this.refreshError ? 'err' : this.stale ? 'stale' : ''}" title="When the server last rebuilt this list${this.stale ? ' — it is rebuilding now' : ''}">${ago ? `updated ${this.esc(ago)}` : ''}${this.esc(note)}</span>`;
  }
  private patchStamp(): void { const el = this.container.querySelector('#qx-stamp'); if (el) el.innerHTML = this.stampHtml(); }
  private patchKpis(): void { const el = this.container.querySelector('#qx-kpis'); if (el) el.innerHTML = this.kpisHtml(); }
  private patchSide(): void {
    const el = this.container.querySelector('#qx-side');
    if (!el) return;
    el.innerHTML = this.sideHtml();
    this.bindSide();
  }
  // The toolbar (search · seg tabs · chips · PMS window · stamp · buttons) is
  // re-rendered in place for fresh counts — never while it holds the focus
  // (typing in #qx-q, the #qx-window menu open, a chip tabbed to), unless a
  // click inside it asked for the rebuild (force). Skipped, only the stamp
  // moves; the counts catch up on the next tick.
  private patchToolbar(force = false): void {
    const el = this.container.querySelector<HTMLElement>('#qx-toolbar');
    if (!el) return;
    // Fix-verify [15]: only a text / select control holds the skip — a clicked
    // chip or ↻ button keeps focus in Chrome, and froze the counts for good.
    const ae = document.activeElement;
    if (!force && ae && el.contains(ae) && /^(INPUT|SELECT|TEXTAREA)$/.test(ae.tagName)) { this.patchStamp(); return; }
    el.innerHTML = this.toolbarHtml();
    this.bindToolbar();
  }

  // ── threads ───────────────────────────────────────────────────────────
  // null = fetch FAILED (rendered as an error + retry, never as "no messages")
  private async fetchThread(row: any, ch: 'sms' | 'pms'): Promise<any | null> {
    let url = '';
    if (ch === 'sms') {
      if (!row.sms) return null;
      url = `${API}/thread?channel=sms&phone_number_id=${encodeURIComponent(row.sms.phone_number_id)}&participants=${encodeURIComponent((row.sms.participants || []).join(','))}`;
    } else {
      if (!row.pms) return null;
      url = `${API}/thread?channel=pms&id=${encodeURIComponent(row.pms.conversation_id)}${row.pms.reservation_id ? `&reservation_id=${encodeURIComponent(row.pms.reservation_id)}` : ''}`;
    }
    return fetch(url, { signal: timeoutSignal(THREAD_TIMEOUT_MS) }).then(r => r.ok ? r.json() : null).catch(() => null);
  }
  private defaultTab(row: any): Tab {
    const chans: string[] = row.channels || [];
    if (row.last_message && row.last_message.channel && chans.includes(row.last_message.channel)) return row.last_message.channel;
    if (row.pms && row.pms.needs_reply && (!row.sms || !row.sms.needs_reply)) return 'pms';
    return (chans[0] as Tab) || 'sms';
  }
  private async openThread(row: any, tab?: Tab): Promise<void> {
    this.selected = row;
    this.tab = tab || this.defaultTab(row);   // R115 guest: the Calendar's conversation hand-off opens the PMS tab
    this.threadData = {}; this.threadErr = {}; this.threadBusy = {};
    this.notes = null; this.noteDraft = '';
    this.draft = ''; this.markDone = false; this.note = ''; this.closeConfirm(); this.detailsOpen = false;
    this.pendingScroll = true;
    this.draw();
    await this.loadTab(this.tab);
  }
  private async loadTab(tab: Tab): Promise<void> {
    const row = this.selected;
    if (!row) return;
    if (tab === 'notes') {
      if (this.notes !== null) return;
      const keys = this.noteKeys(row);
      if (!keys.length) { this.notes = []; this.draw(); return; }   // a synthetic row — no conversation to attach a note to yet
      const j = await fetch(`${API}/notes?ids=${encodeURIComponent(keys.join(','))}`).then(r => r.ok ? r.json() : null).catch(() => null);
      if (this.selected !== row) return;
      this.notes = j && Array.isArray(j.notes) ? j.notes : [];
      this.draw();
      return;
    }
    if (tab === 'issue') return;
    if (this.threadData[tab] !== undefined || this.threadBusy[tab]) return;
    this.threadBusy[tab] = true; this.threadErr[tab] = false;
    this.draw();
    const t = await this.fetchThread(row, tab);
    if (this.selected !== row) return;   // the user moved on
    this.threadBusy[tab] = false;
    if (t) this.threadData[tab] = t; else { this.threadData[tab] = { messages: [] }; this.threadErr[tab] = true; }
    this.pendingScroll = true;
    this.draw();
  }
  // Retry a failed thread load WITHOUT resetting the compose state.
  private async retryThread(tab: 'sms' | 'pms'): Promise<void> {
    if (!this.selected) return;
    delete this.threadData[tab]; this.threadErr[tab] = false;
    await this.loadTab(tab);
  }
  private switchTab(tab: Tab): void {
    if (!this.selected) return;
    this.tab = tab; this.note = ''; this.closeConfirm();
    this.pendingScroll = true;
    this.draw();
    void this.loadTab(tab);
  }

  // ── action items (R54, via /api/quo) ───────────────────────────────────
  private async actionDone(id: string, reopen = false): Promise<void> {
    const r = await fetch(`${QUO}/actions/${encodeURIComponent(id)}/done`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ by: this.myEmail(), reopen }),
    }).catch(() => null);
    if (!r || !r.ok) { alert('Couldn\'t update the action item — try again.'); return; }
    this.actions = this.actions.filter(a => a.id !== id);
    this.patchActions(); this.patchSide();
    await this.loadInbox(true);
  }
  private async addAction(): Promise<void> {
    const s = this.selected;
    const base = s ? `${this.label(s)}: ` : '';
    const note = prompt('Action item — what needs to happen?', base);
    if (note == null || !note.trim()) return;
    const b: any = { note: note.trim(), by: this.myEmail() };
    if (s) {
      b.conversation_id = s.sms ? s.sms.conversation_id : null;
      b.phone_number_id = s.sms ? s.sms.phone_number_id || '' : '';
      b.participants = s.sms ? s.sms.participants || [] : [];
      b.phone = (s.guest && s.guest.phone) || '';
      b.guest_name = (s.guest && s.guest.name) || '';
      b.listing = (s.property && s.property.name) || '';
    }
    const r = await fetch(`${QUO}/actions`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(b) }).catch(() => null);
    if (!r || !r.ok) { alert('Couldn\'t save the action item — try again.'); return; }
    await this.loadInbox(true);
  }
  private myEmail(): string {
    try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.email || '').trim().toLowerCase() || 'unknown'; }
    catch { return 'unknown'; }
  }
  private actionsFor(row: any): any[] {
    if (!row) return [];
    const phone = this.normPhone((row.guest && row.guest.phone) || '');
    const cid = row.sms ? row.sms.conversation_id : null;
    return this.actions.filter(a => (cid && a.conversation_id === cid) || (phone && this.normPhone(a.phone || '') === phone));
  }

  // ── send ──────────────────────────────────────────────────────────────
  private async send(): Promise<void> {
    if (this.sending || !this.selected) return;
    const content = this.draft.trim();
    if (!content) { alert('Type a message first.'); return; }
    if (this.tab === 'pms') {
      // R112: a PMS reply posts to the guest's Airbnb / VRBO / Booking thread —
      // ALWAYS through the confirm step (channel + guest + property + preview).
      if (!this.pmsSend) { this.note = 'PMS replies are switched off on this server (MESSAGING_PMS_SEND) — reply by SMS or in Hostaway.'; this.draw(); return; }
      if (content.length > PMS_MAX) { this.note = `PMS messages are limited to ${PMS_MAX} characters.`; this.draw(); return; }
      // The modal mounts on document.body — the thread panel is NOT redrawn,
      // so it stays scrolled to the reply being answered (Cancel changes nothing).
      this.note = ''; this.container.querySelector('.qx-compose .qx-note')?.remove();
      this.openConfirm();
      return;
    }
    await this.sendSms(content);
  }
  private async sendSms(content: string): Promise<void> {
    const s = this.selected;
    if (!s || !s.sms) return;
    this.sending = true; this.note = ''; this.draw();
    const r = await fetch(`${API}/send`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channel: 'sms', from: s.sms.phone_number_id, to: s.sms.participants || [], content, done: this.markDone }),
    }).catch(() => null);
    const j = r ? await r.json().catch(() => ({})) : null;
    this.sending = false;
    if (!r || !r.ok) { this.note = `Send failed — ${(j && j.error) || 'try again'}${this.maybeSent(r, j) ? ' (it may still have gone out — check the thread before resending)' : ''}`; this.draw(); return; }
    const confirmNote = this.markDone ? 'Sent · marked done.' : 'Sent.';
    this.draft = '';
    const t = this.threadData.sms;
    if (t && Array.isArray(t.messages)) t.messages.push({ kind: 'message', channel: 'sms', direction: 'outgoing', body: content, at: new Date().toISOString(), media: [] });
    this.note = confirmNote; this.pendingScroll = true; this.draw();
    const fresh = await this.fetchThread(s, 'sms');
    if (fresh && Array.isArray(fresh.messages) && fresh.messages.length && this.selected === s) this.threadData.sms = fresh;
    this.note = confirmNote; this.pendingScroll = true; this.draw();
    await this.loadInbox(true);
  }
  private async confirmPmsSend(): Promise<void> {
    const s = this.selected;
    const content = this.draft.trim();
    if (!s || !s.pms || !content || this.sending) return;
    this.sending = true; this.note = '';
    this.setConfirmBusy(true);   // "Sending…" inside the modal — nothing behind it is redrawn
    const r = await fetch(`${API}/send`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channel: 'pms', conversation_id: s.pms.conversation_id, body: content, confirm: true }),
    }).catch(() => null);
    const j = r ? await r.json().catch(() => ({})) : null;
    this.sending = false; this.closeConfirm();
    if (!r || !r.ok) {
      this.note = `Send failed — ${(j && j.error) || 'try again'}${this.maybeSent(r, j) ? ' (it may still have gone out — check the thread in Hostaway before resending)' : ''}`;
      if (r && r.status === 503 && j && j.pms_send === false) this.pmsSend = false;
      this.draw(); return;
    }
    this.draft = '';
    const t = this.threadData.pms;
    if (t && Array.isArray(t.messages)) t.messages.push({ kind: 'message', channel: 'pms', direction: 'outgoing', body: content, at: new Date().toISOString(), images: [] });
    this.note = 'Sent to the guest\'s PMS thread.'; this.pendingScroll = true; this.draw();
    const fresh = await this.fetchThread(s, 'pms');
    if (fresh && Array.isArray(fresh.messages) && fresh.messages.length && this.selected === s) this.threadData.pms = fresh;
    this.note = 'Sent to the guest\'s PMS thread.'; this.pendingScroll = true; this.draw();
    await this.loadInbox(true);
  }

  private async doneWithout(): Promise<void> {
    const s = this.selected;
    if (!s || !s.sms || !s.sms.conversation_id) return;
    if (!confirm('Mark this SMS conversation done (handled elsewhere, e.g. by phone)?')) return;
    const r = await fetch(`${QUO}/conversation-done`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ conversation_id: s.sms.conversation_id }),
    }).catch(() => null);
    if (!r || !r.ok) { const j = r ? await r.json().catch(() => ({})) : null; alert(`Couldn't mark done — ${(j && j.error) || 'try again'}`); return; }
    const id = s.sms.conversation_id;
    if (s.pms) { s.sms.needs_reply = false; s.needs_reply = !!s.pms.needs_reply; }
    else this.rows = this.rows.filter(r => !(r.sms && r.sms.conversation_id === id));
    this.needsReply = this.rows.filter(r => !r.dismissed && r.needs_reply).length;
    if (!s.pms) { this.selected = null; this.threadData = {}; this.draft = ''; this.markDone = false; this.note = ''; }
    this.draw();
    await this.loadInbox(true);
  }

  // "It may still have gone out" is said ONLY when nobody knows (R112 review
  // [16]): no answer at all, a server answer that says so (a timeout / 5xx /
  // network error behind Quo or Hostaway), or a bodyless proxy 5xx. A Quo /
  // Hostaway 4xx comes back as that status with may_have_sent:false — a
  // definite refusal (invalid recipient, unsupported number) the office can
  // resend to a corrected number without hesitating.
  private maybeSent(r: Response | null, j: any): boolean {
    if (!r) return true;
    if (j && typeof j.may_have_sent === 'boolean') return j.may_have_sent;
    return r.status >= 500;
  }

  // ── notes (R112, /api/messaging/note) ──────────────────────────────────
  // Keyed by the CONVERSATION ids, never the row id (R112 review [14]): the
  // same guest is 'guest:<hid>' on All, 'pms:<cid>' on the PMS tab and
  // 'sms:<cid>' once the twin leaves the page, so a note keyed by the row id
  // vanished on the next tab. Every key of the open row goes to the server;
  // it stores under the PMS one and answers the union. A synthetic row
  // (/find or an action item with no conversation id) has no key — the tab
  // says so instead of saving under 'sms:new'.
  private noteKeys(r: any): string[] {
    const keys: string[] = [];
    if (r && r.pms && r.pms.conversation_id) keys.push(`pms:${r.pms.conversation_id}`);
    if (r && r.sms && r.sms.conversation_id) keys.push(`sms:${r.sms.conversation_id}`);
    return keys;
  }
  private async addNote(): Promise<void> {
    const s = this.selected; const text = this.noteDraft.trim();
    const keys = this.noteKeys(s);
    if (!s || !text || !keys.length || this.noteSaving) return;
    this.noteSaving = true; this.draw();
    const r = await fetch(`${API}/note`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: keys, note: text }) }).catch(() => null);
    const j = r ? await r.json().catch(() => ({})) : null;
    this.noteSaving = false;
    if (!r || !r.ok) { alert(`Couldn't save the note — ${(j && j.error) || 'try again'}`); this.draw(); return; }
    this.notes = Array.isArray(j.notes) ? j.notes : [(j && j.note) || { at: new Date().toISOString(), by: this.myEmail(), text }, ...(this.notes || [])];
    this.noteDraft = '';
    this.draw();
  }

  // ── formatting ────────────────────────────────────────────────────────
  private label(r: any): string {
    const name = r.guest && r.guest.name;
    if (name) return name;
    return (r.guest && r.guest.phone) || (r.sms && r.sms.display_number) || 'Unknown';
  }
  private parseT(s: any): number | null {
    let str = String(s || '').trim();
    if (!str) return null;
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(str)) str = str.replace(' ', 'T') + 'Z';
    const ms = Date.parse(str);
    return Number.isFinite(ms) ? ms : null;
  }
  private fmtT(s: any): string {
    const ms = this.parseT(s);
    if (ms == null) return '';
    const d = new Date(ms);
    const todayPT = new Date().toLocaleDateString('en-CA', { timeZone: TZ });
    const dayPT = d.toLocaleDateString('en-CA', { timeZone: TZ });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: TZ });
    return dayPT === todayPT ? time : `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: TZ })} ${time}`;
  }
  private fmtD(s: any): string {
    const d = new Date(`${String(s || '').slice(0, 10)}T00:00:00Z`);
    return isNaN(d.getTime()) ? String(s || '') : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  }
  private initials(r: any): string {
    if (this.isGroup(r)) return String(this.membersOf(r).length || (r.sms && (r.sms.participants || []).length) || '') || '👥';   // R116 #2: a group's avatar is its head count
    const name = String((r.guest && r.guest.name) || '').trim();
    if (!name) return '#';
    const parts = name.split(/\s+/).filter(Boolean);
    return ((parts[0] || '')[0] + ((parts[1] || '')[0] || '')).toUpperCase() || '#';
  }
  private avClass(r: any): string {
    if (this.isGroup(r)) return 'av-grp';
    if (r.sentiment === 'negative') return 'av-neg';
    const s = String((r.guest && r.guest.name) || r.id || '');
    let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return `av-${(h % 4) + 1}`;
  }
  private static readonly PHASE: Record<string, [string, string]> = {
    'in-house': ['In-house', 'pill-green'], arriving: ['Check-in today', 'pill-blue'], departing: ['Check-out today', 'pill-amber'], upcoming: ['Upcoming', 'pill-purple'], past: ['Past', 'pill-gray'],
  };
  private phasePill(r: any): string {
    const ph = r.reservation && r.reservation.phase;
    const p = ph ? QuoInboxPage.PHASE[ph] : null;
    return p ? `<span class="pill ${p[1]}">${p[0]}</span>` : '';
  }
  private static readonly KINDS: Record<string, string> = { vendor: '🔧 vendor', owner: '🏠 owner', cleaner: '🧹 cleaner', staff: '👤 team', group: '👥 group' };
  private kindPill(r: any): string {
    if (this.isGroup(r)) { const n = this.membersOf(r).length; return `<span class="pill pill-blue" title="One Quo group text — everyone on it sees every message">👥 group${n ? ` · ${n}` : ''}</span>`; }
    const k = QuoInboxPage.KINDS[String((r.guest && r.guest.contact_kind) || '')];
    return k ? `<span class="pill pill-gray">${k}</span>` : '';
  }
  private static readonly CH_NAME: Record<string, string> = { airbnbofficial: 'Airbnb', airbnb: 'Airbnb', vrbo: 'VRBO', homeaway: 'VRBO', bookingcom: 'Booking.com', booking: 'Booking.com', direct: 'Direct', expedia: 'Expedia', marriott: 'Homes & Villas' };
  private pmsLabel(r: any): string {
    const cn = String((r.reservation && r.reservation.channel_name) || '').toLowerCase();
    return QuoInboxPage.CH_NAME[cn] || 'PMS';
  }
  private sentimentDot(r: any): string {
    if (!r.sentiment) return '';
    const cls = r.sentiment === 'negative' ? 'neg' : r.sentiment === 'positive' ? 'pos' : 'neu';
    return `<span class="qx-sent ${cls}" title="Guest sentiment (hourly scan): ${this.esc(r.sentiment)}"></span>`;
  }
  private static readonly OUTCOMES: Array<[string, string]> = [
    ['called-back', '📞 Called back'], ['handled', '✅ Handled'], ['not-a-guest', '🚫 Not a guest'], ['no-action', '💤 No action needed'],
  ];
  private outcomeLabel(o: string): string { return (QuoInboxPage.OUTCOMES.find(x => x[0] === o) || [o, o])[1]; }
  private normPhone(raw: string): string {
    const s = String(raw || '').trim();
    if (!s) return '';
    const digits = s.replace(/[^\d+]/g, '');
    if (digits.startsWith('+')) return digits;
    const bare = digits.replace(/\D/g, '');
    if (bare.length === 10) return `+1${bare}`;
    if (bare.length === 11 && bare.startsWith('1')) return `+${bare}`;
    return bare ? `+${bare}` : '';
  }

  // ── R57: NEW-TEXT COMPOSER (unchanged contracts: /api/quo/contacts + /lines, send via /api/messaging/send) ──
  private async openCompose(): Promise<void> {
    this.composing = true; this.composeNote = ''; this.draw();
    if (!this.contacts || !this.lines) {
      const [cj, lj] = await Promise.all([
        fetch(`${QUO}/contacts`).then(r => r.ok ? r.json() : null).catch(() => null),
        fetch(`${QUO}/lines`).then(r => r.ok ? r.json() : null).catch(() => null),
      ]);
      this.contacts = cj && Array.isArray(cj.contacts) ? cj.contacts : null;
      this.lines = lj && Array.isArray(lj.lines) ? lj.lines : null;
      if (!this.contacts || !this.lines) this.composeNote = 'Couldn\'t load contacts/lines — close and reopen to retry.';
      if (!this.composeLine && this.lines && this.lines.length) this.composeLine = (this.lineFilter && this.lines.some((l: any) => String(l.id) === this.lineFilter)) ? this.lineFilter : this.lines[0].id;
      if (this.composing) this.draw();
    }
  }
  private closeCompose(): void { this.composing = false; this.composeNote = ''; this.draw(); }
  private resolveComposeTo(): string {
    const v = this.composeTo.trim();
    if (!v) return '';
    const hit = (this.contacts || []).find(ct => `${ct.name} — ${ct.phone}` === v || ct.name === v || ct.phone === v);
    if (hit) return hit.phone;
    const p = this.normPhone(v);
    return /^\+\d{10,15}$/.test(p) ? p : '';
  }
  private async sendNew(): Promise<void> {
    if (this.composeSending) return;
    const phone = this.resolveComposeTo();
    const content = this.composeMsg.trim();
    const from = this.composeLine || (this.lines && this.lines[0] && this.lines[0].id) || '';
    if (!phone) { this.composeNote = 'Pick a contact or enter a valid phone number.'; this.draw(); return; }
    if (!content) { this.composeNote = 'Type a message first.'; this.draw(); return; }
    if (!from) { this.composeNote = 'No Quo line to send from — check the connection.'; this.draw(); return; }
    this.composeSending = true; this.composeNote = ''; this.draw();
    const r = await fetch(`${API}/send`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ channel: 'sms', from, to: [phone], content }) }).catch(() => null);
    const j = r ? await r.json().catch(() => ({})) : null;
    this.composeSending = false;
    if (!r || !r.ok) { this.composeNote = `Send failed — ${(j && j.error) || 'try again'}${this.maybeSent(r, j) ? ' (it may still have gone out — check the thread before resending)' : ''}`; this.draw(); return; }
    this.composing = false; this.composeTo = ''; this.composeMsg = ''; this.composeNote = '';
    await this.loadInbox(true);
    const row = this.findRow(phone);
    if (row) void this.openThread(row); else this.draw();
  }

  // ── list ──────────────────────────────────────────────────────────────
  private visibleRows(): any[] {
    let rows = this.rows;
    if (this.lineFilter) rows = rows.filter(r => !r.sms || String(r.sms.phone_number_id || '') === this.lineFilter);
    const inHouse = (r: any) => r.reservation && ['in-house', 'arriving', 'departing'].includes(r.reservation.phase);
    const team = (r: any) => !!QuoInboxPage.KINDS[String((r.guest && r.guest.contact_kind) || '')];
    if (this.chip === 'needs') rows = rows.filter(r => r.needs_reply && !r.dismissed);
    else if (this.chip === 'inhouse') rows = rows.filter(inHouse);
    else if (this.chip === 'arriving') rows = rows.filter(r => r.reservation && r.reservation.phase === 'arriving');
    else if (this.chip === 'negative') rows = rows.filter(r => r.sentiment === 'negative');
    else if (this.chip === 'team') rows = rows.filter(team);
    const q = this.q.trim().toLowerCase();
    if (q) rows = rows.filter(r => [this.label(r), r.property && r.property.name, r.guest && r.guest.phone, r.sms && r.sms.line, r.last_message && r.last_message.body, r.reservation && r.reservation.confirmation_code].some(x => String(x || '').toLowerCase().includes(q)));
    return rows;
  }
  private rowHtml(r: any): string {
    const on = this.selected && this.selected.id === r.id;
    const sid = r.sms ? r.sms.conversation_id : '';
    if (sid && this.dismissing === sid) {
      return `
        <div class="qx-row qx-dismissing">
          <div class="qx-row-name" style="margin-bottom:6px;">${this.esc(this.label(r))} — what happened?</div>
          <div style="display:flex; flex-wrap:wrap; gap:5px;">
            ${QuoInboxPage.OUTCOMES.map(([k, l]) => `<button type="button" class="btn btn-secondary btn-sm" data-qx-outcome="${this.esc(k)}" data-qx-conv="${this.esc(sid)}">${l}</button>`).join('')}
            <button type="button" class="btn btn-secondary btn-sm" data-qx-canceldismiss>Cancel</button>
          </div>
        </div>`;
    }
    if (r.dismissed) {
      return `
        <div class="qx-row qx-gone">
          <div class="qx-row-main">
            <div class="qx-row-top"><span class="qx-row-name">${this.esc(this.label(r))}</span>${sid ? `<button type="button" class="btn btn-secondary btn-sm" data-qx-restore="${this.esc(sid)}">↩ Restore</button>` : ''}</div>
            <div class="qx-row-prev">${this.esc(this.outcomeLabel(r.dismiss_outcome))} · ${this.esc((r.dismiss_by || '').split('@')[0])} · ${this.esc(this.fmtT(r.dismiss_at))}</div>
          </div>
        </div>`;
    }
    const lm = r.last_message;
    const preview = lm
      ? (lm.direction === 'incoming' ? (lm.from_name ? `${lm.from_name}: ` : '') : 'You: ') + String(lm.body || (lm.media ? `📷 ${lm.media} photo${lm.media === 1 ? '' : 's'}` : '')).slice(0, 110)
      : (r.snippet === 'capped' ? (r.needs_reply ? 'New message from the guest' : 'No preview yet') : 'No messages');
    const dates = r.reservation && r.reservation.check_in ? `<span class="qx-dates">${this.esc(this.fmtD(r.reservation.check_in))} → ${this.esc(this.fmtD(r.reservation.check_out))}</span>` : '';
    const chans = (r.channels || []).map((c: string) => c === 'sms' ? `<span class="qx-ch sms" title="Quo SMS${r.sms && r.sms.line ? ` · ${this.esc(r.sms.line)}` : ''}">SMS</span>` : `<span class="qx-ch pms" title="PMS thread via Hostaway">${this.esc(this.pmsLabel(r))}</span>`).join('');
    return `
        <div class="qx-row ${on ? 'on' : ''} ${r.needs_reply ? 'needs' : ''}" data-qx-open="${this.esc(r.id)}" role="button" tabindex="0">
          <div class="qx-av ${this.avClass(r)}">${this.esc(this.initials(r))}</div>
          <div class="qx-row-main">
            <div class="qx-row-top">
              <span class="qx-row-name">${r.needs_reply ? '<span class="qx-dot" title="Waiting on a reply"></span>' : ''}${this.esc(this.label(r))}${this.sentimentDot(r)}</span>
              <span class="qx-row-time">${this.esc(this.fmtT(r.last_activity_at))}</span>
              ${sid ? `<span class="qx-x" data-qx-dismiss="${this.esc(sid)}" role="button" tabindex="0" aria-label="Dismiss with an outcome" title="Dismiss with an outcome">✕</span>` : ''}
            </div>
            <div class="qx-row-prev">${this.esc(preview)}</div>
            <div class="qx-row-meta">${r.property && r.property.name ? `<span class="qx-prop">${this.esc(r.property.name)}</span>` : ''}${dates}${this.phasePill(r)}${this.kindPill(r)}<span class="qx-chs">${chans}</span></div>
          </div>
        </div>`;
  }
  private lineChipsHtml(): string {
    if (!this.configured || !this.configured.sms || this.channel === 'pms') return '';
    const byLine = new Map<string, { label: string; n: number; need: number }>();
    for (const l of (this.lines || [])) { const id = String(l.id || ''); if (id) byLine.set(id, { label: String(l.name || l.number || id), n: 0, need: 0 }); }
    for (const r of this.rows) {
      if (!r.sms) continue;
      const id = String(r.sms.phone_number_id || ''); if (!id) continue;
      const e = byLine.get(id) || { label: String(r.sms.line || id), n: 0, need: 0 };
      if (!r.dismissed) { e.n++; if (r.sms.needs_reply) e.need++; }
      byLine.set(id, e);
    }
    if (byLine.size < 2 && !this.lineFilter) return '';
    if (this.lineFilter) for (const [id, e] of byLine) if (id !== this.lineFilter) e.need = this.lineNeed.get(id) || 0;
    const all = [...byLine.values()].reduce((a, e) => a + e.need, 0);
    const chip = (id: string, label: string, need: number) => `<button type="button" class="chip qx-linechip ${this.lineFilter === id ? 'active' : ''}" data-qx-line="${this.esc(id)}" title="${id ? 'Show only this SMS line — pulls its own most-recent 30 threads' : 'Every SMS line'}">${this.esc(label)}${need ? ` <span class="chip-count">${need}</span>` : ''}</button>`;
    const note = this.lineFilter ? '' : (this.perLine ? `<div class="qx-lines-note">the ${this.perLine} most recent SMS threads on each line — pick a line for its full list</div>` : '');
    const errs = this.fetchErrors.length ? `<div class="qx-lines-note err">⚠ couldn't read ${this.fetchErrors.map(e => this.esc(e.line)).join(', ')} — showing what loaded</div>` : '';
    return `<div class="qx-lines">${chip('', 'All lines', all)}${[...byLine.entries()].sort((a, b) => a[1].label.localeCompare(b[1].label)).map(([id, e]) => chip(id, e.label, e.need)).join('')}<button type="button" class="chip qx-linechip" data-qx-diag title="Where are a phone number's texts landing? Checks every Quo line">🩺 find a number</button></div>${note}${errs}`;
  }
  private listHtml(): string {
    const rows = this.visibleRows();
    const attention = rows.filter(r => !r.dismissed && r.needs_reply);
    const active = rows.filter(r => !r.dismissed && !r.needs_reply && r.last_message);
    const quiet = rows.filter(r => !r.dismissed && !r.needs_reply && !r.last_message);
    const gone = rows.filter(r => r.dismissed);
    const parts: string[] = [];
    parts.push(this.lineChipsHtml());
    parts.push(`<div class="qx-list-head">Inbox <span class="qx-list-n">${rows.filter(r => !r.dismissed).length}</span>${attention.length ? `<span class="qx-nr">${attention.length} awaiting reply</span>` : ''}</div>`);
    if (this.errors.length) parts.push(`<div class="qx-lines-note err" style="padding:0 14px;">⚠ ${this.errors.map(e => `${this.esc(e.channel.toUpperCase())}: ${this.esc(e.error)}`).join(' · ')}</div>`);
    if (!rows.length) parts.push(`<div class="qx-empty">${this.q || this.chip ? 'Nothing matches this filter.' : this.lineFilter ? 'No conversations on this line.' : 'No conversations.'}</div>`);
    parts.push(attention.map(r => this.rowHtml(r)).join(''));
    parts.push(active.map(r => this.rowHtml(r)).join(''));
    if (quiet.length) {
      parts.push(`<button type="button" class="qx-group" data-qx-togglequiet>📞 Calls &amp; no-message numbers (${quiet.length}) ${this.showQuiet ? '▾' : '▸'}</button>`);
      if (this.showQuiet) parts.push(quiet.map(r => this.rowHtml(r)).join(''));
    }
    if (gone.length) {
      parts.push(`<button type="button" class="qx-group" data-qx-toggledismissed>🗂 Dismissed (${gone.length}) ${this.showDismissed ? '▾' : '▸'}</button>`);
      if (this.showDismissed) parts.push(gone.map(r => this.rowHtml(r)).join(''));
    }
    return parts.join('');
  }
  private patchList(): void {
    const el = this.container.querySelector('#qx-list');
    if (!el) { this.draw(); return; }
    el.innerHTML = this.listHtml();
    this.bindRows();
  }
  private patchActions(): void {
    const host = this.container.querySelector('#qx-actions-host');
    if (!host) return;
    host.innerHTML = this.actionsStrip();
    this.bindActions();
  }

  // ── KPIs + toolbar ─────────────────────────────────────────────────────
  private kpisHtml(): string {
    const c = this.counts || {};
    const card = (label: string, ico: string, v: number, cap: string, cls = '') => `
      <div class="kpi-card ${cls}">
        <div class="kpi-card-top"><span class="kpi-card-label">${label}</span><span class="kpi-card-ic">${icon(ico, 17)}</span></div>
        <div class="kpi-card-value">${v}</div>
        <div class="kpi-card-cap">${cap}</div>
      </div>`;
    const needs = Number(c.needs_reply) || 0, pms = Number(c.needs_pms) || 0, neg = Number(c.in_house_negative) || 0;
    return `
      ${card('Awaiting reply', 'chat', needs, needs ? 'guests waiting on us right now' : 'nobody is waiting on us', needs ? 'tone-bad' : 'tone-good')}
      ${card('PMS awaiting', 'home', pms, 'Airbnb · VRBO · Booking threads')}
      ${card('In-house negative', 'heart', neg, neg ? 'unhappy guests staying tonight' : 'no unhappy in-house guests', neg ? 'tone-warn' : '')}
      ${card('Action items', 'target', this.actions.length, this.actions.length ? 'open follow-ups' : 'nothing open')}`;
  }
  private toolbarHtml(): string {
    const c = this.counts || {};
    const seg = (v: Channel, label: string, n: number | null, on = true) => on ? `<button type="button" class="seg-tab ${this.channel === v ? 'active' : ''}" data-qx-channel="${v}">${label}${n != null ? ` <span class="qx-n">${n}</span>` : ''}</button>` : '';
    const chip = (v: string, label: string, n: number) => `<button type="button" class="chip ${this.chip === v ? 'active' : ''}" data-qx-chip="${v}">${label}${n ? ` <span class="chip-count">${n}</span>` : ''}</button>`;
    const cf = this.configured || { sms: false, pms: false };
    return `
      <div class="qx-bar">
        <input class="input qx-search" id="qx-q" type="search" placeholder="Search guest, property, phone, text…" value="${this.esc(this.q)}" autocomplete="off">
        <div class="seg-tabs" role="tablist">${seg('all', 'All', null)}${seg('sms', 'SMS', c.sms ?? null, cf.sms)}${seg('pms', 'PMS', c.pms ?? null, cf.pms)}</div>
        <div class="qx-chips">
          ${chip('needs', 'Needs reply', Number(c.needs_reply) || 0)}${chip('inhouse', 'In-house', Number(c.in_house) || 0)}${chip('arriving', 'Arriving', Number(c.arriving) || 0)}${chip('negative', 'Negative', Number(c.negative) || 0)}${chip('team', 'Team & vendors', Number(c.team) || 0)}
        </div>
      </div>
      <div class="qx-bar qx-bar2">
        ${cf.pms && this.channel !== 'sms' ? `<label class="qx-win" title="How far back the PMS conversations go">PMS window <select id="qx-window" class="input">${[7, 14, 30].map(w => `<option value="${w}" ${this.window === w ? 'selected' : ''}>${w} days</option>`).join('')}</select></label>` : ''}
        <span class="qx-sub">${this.needsReply ? `${this.needsReply} waiting · ` : ''}newest activity first · times in PT</span>
        <span class="qx-spacer"></span>
        ${cf.sms ? `<button type="button" class="btn btn-secondary" data-qx-newtext>✉️ New text</button>` : ''}
        <button type="button" class="btn btn-secondary" data-qx-addaction>＋ Action item</button>
        <span id="qx-stamp">${this.stampHtml()}</span>
        <button type="button" class="btn btn-secondary" data-qx-refresh title="Pull the list again now">↻</button>
      </div>`;
  }

  // ── draw ──────────────────────────────────────────────────────────────
  private draw(): void {
    const body = this.bodyEl;
    if (!body || !body.isConnected) return;
    if (this.loading) { body.innerHTML = `<div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div>`; return; }

    const focusNote = this.focusNote ? `<div class="qx-focus-note">${this.esc(this.focusNote)}</div>` : '';
    const head = `
      <div class="page-intro qx-intro">
        <div class="dash-head">
          <h1>Messaging</h1>
          <p>Every guest conversation in one inbox — Quo texts and the PMS threads (Airbnb, VRBO, Booking) side by side, the ones a guest is waiting on first. Open a thread to read, reply on the right channel and see the stay, the contact and the sentiment next to it.</p>
        </div>
      </div>`;
    if (this.configured && !this.configured.sms && !this.configured.pms) {
      body.innerHTML = head + emptyState('Messaging not connected', 'Neither Quo (QUO_API_KEY) nor Hostaway (HOSTAWAY_ACCOUNT_ID / HOSTAWAY_API_KEY) is configured on the server yet — set either in Railway and this inbox lights up.', '📵') + focusNote;
      return;
    }
    if (this.loadError) {
      body.innerHTML = head + emptyState('Couldn\'t load the inbox', 'The server didn\'t respond in time — check the Quo / Hostaway keys and try again.', '⚠️') + focusNote +
        `<div style="text-align:center; margin-top:12px;"><button type="button" class="btn btn-secondary" data-qx-retry>Retry</button></div>`;
      this.bind();
      return;
    }
    this.focusNote = '';
    // A routine redraw keeps the thread where the reader left it; only a real
    // open / tab switch / send (pendingScroll, consumed in bind) jumps to the newest.
    const prevThread = body.querySelector<HTMLElement>('#qx-thread');
    const keepScroll = prevThread && !this.pendingScroll ? prevThread.scrollTop : null;

    body.innerHTML = `
      <style>${this.css()}</style>
      ${head}
      ${focusNote}
      <div class="kpi-grid qx-kpis" id="qx-kpis">${this.kpisHtml()}</div>
      <div id="qx-toolbar">${this.toolbarHtml()}</div>
      ${this.composing ? this.composeNewHtml() : ''}
      <div id="qx-actions-host">${this.actionsStrip()}</div>
      <div class="qx-card ${this.selected ? 'has-sel' : ''}">
        <div class="qx-list" id="qx-list">${this.listHtml()}</div>
        <div class="qx-panel" id="qx-panel">${this.selected ? this.threadPanel() : `<div class="qx-empty qx-pick"><div class="qx-pick-ic">${icon('chat', 22)}</div>Pick a conversation to read and reply.</div>`}</div>
        <div class="qx-side ${this.detailsOpen ? 'open' : ''}" id="qx-side">${this.sideHtml()}</div>
      </div>`;
    this.bind();
    if (keepScroll != null) { const th = body.querySelector<HTMLElement>('#qx-thread'); if (th) th.scrollTop = keepScroll; }
  }

  private css(): string {
    return `
        .qx-intro .dash-head { margin-bottom:16px; }
        .qx-focus-note { text-align:center; margin:0 0 12px; font-size:12.5px; color:var(--warning-text); }
        .kpi-grid.qx-kpis { gap:12px; margin-bottom:14px; }
        .qx-kpis .kpi-card { padding:14px 16px; }
        .qx-kpis .kpi-card-value { font-size:24px; margin-top:10px; }
        .qx-kpis .kpi-card-ic { width:34px; height:34px; border-radius:10px; }
        .qx-kpis .kpi-card-cap { margin-top:4px; }
        .qx-bar { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:10px; }
        .qx-bar2 { margin-bottom:12px; }
        .qx-sub { font-size:12px; color:var(--text-muted); }
        .qx-search { width:220px; flex:none; }
        .qx-chips { display:flex; gap:6px; flex-wrap:wrap; }
        .qx-n { font-variant-numeric:tabular-nums; opacity:.75; font-weight:500; }
        .qx-win { display:inline-flex; align-items:center; gap:6px; font-size:12px; color:var(--text-muted); }
        .qx-win .input { width:auto; padding:6px 10px; }
        .qx-spacer { flex:1; }
        .qx-card { background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow-card); overflow:hidden; display:grid; grid-template-columns:minmax(300px,340px) minmax(0,1fr) 320px; height:calc(100vh - 340px); min-height:540px; }
        .qx-list { border-right:1px solid var(--border); overflow-y:auto; min-width:0; }
        .qx-list-head { padding:11px 14px; font-size:12.5px; font-weight:700; border-bottom:1px solid var(--border); position:sticky; top:0; background:var(--card); z-index:1; display:flex; align-items:center; gap:8px; }
        .qx-list-n { font-weight:500; color:var(--text-faint); font-variant-numeric:tabular-nums; }
        .qx-nr { background:var(--danger-soft); color:var(--danger-text); border-radius:var(--radius-pill); padding:1px 8px; font-size:11px; margin-left:auto; }
        .qx-lines { display:flex; gap:5px; flex-wrap:wrap; padding:8px 10px 4px; }
        .qx-linechip { padding:4px 10px; font-size:11.5px; }
        .qx-lines-note { font-size:11px; color:var(--text-faint); margin:2px 10px 6px; }
        .qx-lines-note.err { color:var(--danger-text); }
        .qx-row { display:flex; gap:10px; width:100%; text-align:left; border-bottom:1px solid var(--border-soft); background:none; padding:11px 14px; cursor:pointer; transition:background .12s ease; }
        .qx-row:hover { background:var(--bg-subtle); }
        .qx-row.on { background:var(--accent-soft); }
        .qx-row.needs { box-shadow:inset 3px 0 0 var(--danger); }
        .qx-row:focus-visible { outline:none; box-shadow:inset 0 0 0 2px var(--accent); }
        .qx-av { flex:none; width:38px; height:38px; border-radius:var(--radius-pill); display:inline-flex; align-items:center; justify-content:center; font-size:12.5px; font-weight:700; letter-spacing:.02em; }
        .qx-av.av-1 { background:var(--accent-soft); color:var(--accent); }
        .qx-av.av-2 { background:var(--success-soft); color:var(--success-text); }
        .qx-av.av-3 { background:var(--warning-soft); color:var(--warning-text); }
        .qx-av.av-4 { background:var(--info-soft); color:var(--info-text); }
        .qx-av.av-neg { background:var(--danger-soft); color:var(--danger-text); box-shadow:0 0 0 2px var(--danger); }
        .qx-av.lg { width:44px; height:44px; font-size:14px; }
        /* R116 #2: Quo group threads (vendor group texts) */
        .qx-av.av-grp { background:var(--accent); color:var(--card); box-shadow:0 0 0 2px var(--accent-soft); }
        .qx-av.av-grp::after { content:'👥'; font-size:9px; margin-left:1px; }
        .qx-av.sm { width:30px; height:30px; font-size:11px; }
        .qx-av.av-grp-m { background:var(--bg-subtle); color:var(--text-muted); border:1px solid var(--border); }
        .qx-member { display:flex; align-items:center; gap:10px; padding:6px 0; border-top:1px solid var(--border-soft); }
        .qx-member:first-of-type { border-top:none; }
        .qx-mtxt { min-width:0; display:flex; flex-direction:column; }
        .qx-mname { font-size:12.5px; font-weight:600; color:var(--text); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .qx-msub { font-size:11.5px; color:var(--text-muted); }
        .qx-bubble-who { font-size:11px; font-weight:700; color:var(--accent); margin-bottom:2px; white-space:normal; }
        .qx-who-kind { font-weight:600; color:var(--text-faint); font-size:10.5px; }
        .qx-grp-name { min-width:0; overflow:hidden; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow-wrap:anywhere; }
        .qx-row-main { flex:1; min-width:0; }
        .qx-row-top { display:flex; align-items:baseline; gap:8px; }
        .qx-row-name { font-size:13px; font-weight:600; color:var(--text); flex:1; min-width:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .qx-row.needs .qx-row-name { font-weight:700; }
        .qx-dot { display:inline-block; width:7px; height:7px; border-radius:50%; background:var(--danger); margin-right:6px; vertical-align:middle; }
        .qx-sent { display:inline-block; width:7px; height:7px; border-radius:50%; margin-left:6px; vertical-align:middle; background:var(--text-faint); }
        .qx-sent.neg { background:var(--danger); } .qx-sent.pos { background:var(--success); }
        .qx-row-time { font-size:11px; color:var(--text-faint); flex:none; font-variant-numeric:tabular-nums; }
        .qx-row-prev { font-size:12px; color:var(--text-muted); margin-top:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .qx-row.needs .qx-row-prev { color:var(--text); }
        .qx-row-meta { display:flex; align-items:center; gap:6px; flex-wrap:wrap; margin-top:5px; font-size:11px; color:var(--text-muted); }
        .qx-prop { max-width:170px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .qx-dates { font-variant-numeric:tabular-nums; color:var(--text-faint); }
        .qx-chs { display:inline-flex; gap:4px; margin-left:auto; }
        .qx-ch { font-size:9.5px; font-weight:700; letter-spacing:.04em; padding:1px 6px; border-radius:var(--radius-pill); border:1px solid var(--border); color:var(--text-muted); background:var(--card); }
        .qx-ch.pms { border-color:var(--accent-border); color:var(--accent); background:var(--accent-soft); }
        .qx-x { flex:none; color:var(--text-faint); font-size:12px; padding:0 4px; border-radius:4px; cursor:pointer; visibility:hidden; }
        .qx-row:hover .qx-x, .qx-row:focus-within .qx-x, .qx-x:focus { visibility:visible; }
        .qx-x:hover, .qx-x:focus { color:var(--danger-text); background:var(--danger-soft); outline:none; }
        @media (hover: none) { .qx-x { visibility:visible; } }
        .qx-group { display:block; width:100%; text-align:left; border:none; border-bottom:1px solid var(--border-soft); background:var(--bg-subtle); padding:8px 14px; font-size:12px; font-weight:600; color:var(--text-muted); cursor:pointer; font-family:inherit; }
        .qx-row.qx-gone { opacity:.75; background:var(--bg-subtle); cursor:default; }
        .qx-row.qx-dismissing { background:var(--warning-soft); display:block; cursor:default; }
        .qx-empty { padding:24px; text-align:center; color:var(--text-muted); font-size:13px; }
        .qx-pick { align-self:center; margin:auto; }
        .qx-pick-ic { width:44px; height:44px; border-radius:12px; background:var(--accent-soft); color:var(--accent); display:inline-flex; align-items:center; justify-content:center; margin-bottom:10px; }
        /* thread */
        .qx-panel { display:flex; flex-direction:column; min-width:0; overflow:hidden; }
        .qx-panel-head { display:flex; align-items:center; gap:12px; padding:12px 16px; border-bottom:1px solid var(--border); }
        .qx-head-txt { flex:1; min-width:0; }
        .qx-head-name { font-weight:700; font-size:15px; color:var(--text); display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
        .qx-head-sub { font-size:11.5px; color:var(--text-muted); margin-top:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .qx-head-btns { display:flex; gap:6px; flex:none; }
        .qx-tabs { display:flex; align-items:center; gap:10px; padding:8px 16px; border-bottom:1px solid var(--border-soft); background:var(--bg-subtle); }
        .qx-tabs .seg-tabs { padding:2px; }
        .qx-tabs .seg-tab { padding:5px 12px; }
        .qx-tab-dot { display:inline-block; width:6px; height:6px; border-radius:50%; background:var(--danger); }
        .qx-guest { display:none; flex-wrap:wrap; gap:12px; padding:7px 16px; border-bottom:1px solid var(--border-soft); font-size:12px; color:var(--text-muted); }
        .qx-thread { flex:1; overflow-y:auto; padding:14px 16px; display:flex; flex-direction:column; gap:8px; }
        .qx-pt { align-self:center; font-size:10.5px; color:var(--text-faint); }
        .qx-bubble { max-width:76%; padding:8px 12px; border-radius:14px; font-size:13px; line-height:1.45; white-space:pre-wrap; word-break:break-word; }
        .qx-bubble.in { align-self:flex-start; background:var(--bg-subtle); color:var(--text); border:1px solid var(--border-soft); border-bottom-left-radius:4px; }
        .qx-bubble.out { align-self:flex-end; background:var(--accent); color:var(--card); border-bottom-right-radius:4px; }
        .qx-bubble-t { font-size:10.5px; opacity:.7; margin-top:3px; white-space:normal; }
        .qx-bubble a { color:inherit; }
        .qx-call { align-self:center; font-size:11.5px; color:var(--text-muted); background:var(--bg-subtle); border-radius:var(--radius-pill); padding:3px 12px; }
        .qx-call.missed { color:var(--danger-text); background:var(--danger-soft); }
        .qx-compose { border-top:1px solid var(--border); padding:10px 12px; background:var(--card); }
        .qx-compose-ch { font-size:11.5px; color:var(--text-muted); margin-bottom:6px; display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
        .qx-compose-ch b { color:var(--text); }
        .qx-compose textarea { width:100%; border:1px solid var(--border); border-radius:var(--radius-sm); padding:9px 11px; font-size:13px; font-family:inherit; resize:vertical; box-sizing:border-box; background:var(--card); color:var(--text); }
        .qx-compose textarea:disabled { background:var(--bg-subtle); color:var(--text-faint); }
        .qx-compose-row { display:flex; align-items:center; gap:12px; margin-top:8px; flex-wrap:wrap; }
        .qx-note { font-size:12px; color:var(--text-muted); }
        .qx-note.err { color:var(--danger-text); }
        .qx-off { font-size:12px; color:var(--warning-text); background:var(--warning-soft); border:1px solid var(--warning); border-radius:var(--radius-sm); padding:7px 10px; }
        /* issue + notes tabs */
        .qx-sec { flex:1; overflow-y:auto; padding:14px 16px; display:flex; flex-direction:column; gap:10px; }
        .qx-box { border:1px solid var(--border); border-radius:var(--radius-sm); padding:10px 12px; background:var(--card); font-size:12.5px; line-height:1.5; }
        .qx-box .h { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:var(--text-muted); margin-bottom:4px; }
        .qx-notes-list { display:flex; flex-direction:column; gap:8px; }
        .qx-noteitem { background:var(--bg-subtle); border:1px solid var(--border-soft); border-radius:var(--radius-sm); padding:8px 10px; font-size:12.5px; white-space:pre-wrap; }
        .qx-noteitem .m { font-size:10.5px; color:var(--text-faint); margin-top:4px; }
        /* right panel */
        .qx-side { border-left:1px solid var(--border); overflow-y:auto; background:var(--bg-subtle); min-width:0; display:flex; flex-direction:column; }
        .qx-side-head { display:flex; align-items:center; gap:8px; padding:12px 16px; border-bottom:1px solid var(--border); font-size:12.5px; font-weight:700; background:var(--card); position:sticky; top:0; }
        .qx-side-close { display:none; margin-left:auto; width:28px; height:28px; border-radius:var(--radius-pill); border:none; background:transparent; color:var(--text-muted); cursor:pointer; font-size:15px; }
        .qx-side-body { padding:14px 16px 24px; display:flex; flex-direction:column; gap:12px; }
        .qx-sc { background:var(--card); border:1px solid var(--border); border-radius:var(--radius-sm); padding:12px; }
        .qx-sc .h { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:var(--text-faint); margin-bottom:8px; }
        .qx-prop-name { font-size:13.5px; font-weight:700; color:var(--text); }
        .qx-dl3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:8px; margin-top:10px; padding-top:10px; border-top:1px solid var(--border-soft); }
        .qx-dl { font-size:10px; font-weight:700; letter-spacing:.06em; text-transform:uppercase; color:var(--text-faint); }
        .qx-dv { font-size:13px; font-weight:600; color:var(--text); margin-top:2px; }
        .qx-kv { display:flex; justify-content:space-between; gap:10px; font-size:12px; padding:4px 0; }
        .qx-kv span:first-child { color:var(--text-muted); }
        .qx-kv span:last-child { color:var(--text); font-weight:500; text-align:right; min-width:0; overflow:hidden; text-overflow:ellipsis; }
        .qx-contact { display:flex; align-items:center; gap:8px; font-size:12.5px; padding:4px 0; }
        .qx-contact .v { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; color:var(--text); }
        .qx-copy { border:1px solid var(--border); background:var(--card); border-radius:6px; font-size:10.5px; padding:2px 7px; cursor:pointer; color:var(--text-muted); font-family:inherit; }
        .qx-copy:hover { border-color:var(--accent); color:var(--accent); }
        .qx-sc .btn { width:100%; justify-content:center; margin-top:8px; }
        .qx-sent-card { border-color:var(--danger-border); box-shadow:inset 4px 0 0 var(--danger); }
        .qx-sent-card.pos { border-color:var(--success-border); box-shadow:inset 4px 0 0 var(--success); }
        .qx-sum { font-size:12.5px; color:var(--text); line-height:1.5; }
        .qx-eng { font-size:12px; color:var(--text-muted); margin-top:6px; line-height:1.5; }
        .qx-issues { margin:8px 0 0; padding-left:16px; font-size:12px; color:var(--text); }
        .qx-act-mini { display:flex; align-items:flex-start; gap:8px; font-size:12px; padding:5px 0; border-top:1px solid var(--border-soft); }
        .qx-act-mini:first-of-type { border-top:none; }
        .qx-link { font:inherit; font-weight:600; color:var(--accent); background:none; border:none; padding:0; cursor:pointer; text-decoration:underline; text-underline-offset:2px; }
        /* action strip (R54) */
        .qx-actions { border:1px solid var(--border); border-radius:var(--radius); margin-bottom:12px; background:var(--card); overflow:hidden; box-shadow:var(--shadow-card); }
        .qx-actions-head { padding:9px 14px; font-size:12.5px; font-weight:700; border-bottom:1px solid var(--border); display:flex; align-items:center; gap:8px; }
        .qx-act { display:flex; align-items:center; gap:9px; padding:7px 12px; border-bottom:1px solid var(--border-soft); font-size:12.5px; }
        .qx-act:last-child { border-bottom:none; }
        .qx-act.auto { background:var(--warning-soft); }
        .qx-act-check { border:none; background:none; cursor:pointer; font-size:15px; line-height:1; padding:2px; }
        .qx-act-note { flex:1; color:var(--text); }
        .qx-act-note[data-qx-actopen]:hover { text-decoration:underline; }
        .qx-act-meta { font-size:10.5px; color:var(--text-faint); flex:none; }
        /* new text (R57) */
        .qx-newtext { border:1px solid var(--border); border-radius:var(--radius); padding:10px 12px; margin-bottom:12px; background:var(--card); box-shadow:var(--shadow-card); }
        .qx-newtext-row { display:flex; flex-wrap:wrap; gap:8px; align-items:center; margin-bottom:8px; }
        .qx-newtext input, .qx-newtext select { border:1px solid var(--border); border-radius:var(--radius-sm); padding:8px 10px; font-size:13px; font-family:inherit; box-sizing:border-box; background:var(--card); color:var(--text); }
        .qx-newtext textarea { width:100%; border:1px solid var(--border); border-radius:var(--radius-sm); padding:9px 11px; font-size:13px; font-family:inherit; resize:vertical; box-sizing:border-box; }
        .qx-stamp { font-size:11px; color:var(--text-faint); white-space:nowrap; }
        .qx-stamp.stale { color:var(--accent); }
        .qx-stamp.err { color:var(--danger-text); }
        .qx-back { display:none; }
        .qx-details-btn { display:none; }
        /* confirm modal (PMS send) */
        .qx-ovl { position:fixed; inset:0; background:rgba(15,23,42,.45); z-index:9500; display:flex; align-items:center; justify-content:center; padding:16px; }
        .qx-modal { background:var(--card); border-radius:var(--radius); box-shadow:var(--shadow-pop); width:min(520px, 100%); padding:20px 22px; }
        .qx-modal h3 { margin:0 0 6px; font-size:16px; font-weight:700; color:var(--text); }
        .qx-modal p { margin:0 0 12px; font-size:12.5px; color:var(--text-muted); line-height:1.5; }
        .qx-modal .qx-kv { font-size:12.5px; }
        .qx-preview { border:1px solid var(--border); background:var(--bg-subtle); border-radius:var(--radius-sm); padding:10px 12px; font-size:13px; margin:10px 0 12px; white-space:pre-wrap; word-break:break-word; }
        .qx-modal-btns { display:flex; justify-content:flex-end; gap:8px; }
        @media (max-width:1100px) {
          .qx-card { grid-template-columns:minmax(280px,320px) minmax(0,1fr); }
          .qx-side { position:fixed; top:0; right:0; bottom:0; width:400px; max-width:100vw; background:var(--card); box-shadow:var(--shadow-pop); z-index:150; transform:translateX(105%); transition:transform .18s ease; border-left:1px solid var(--border); }
          .qx-side.open { transform:none; }
          .qx-side-close { display:inline-flex; align-items:center; justify-content:center; }
          .qx-details-btn { display:inline-flex; }
          .qx-guest { display:flex; }
        }
        @media (max-width:760px) {
          .qx-intro .dash-head p { display:none; }
          .qx-intro .dash-head { margin-bottom:10px; }
          .qx-kpis .kpi-card { padding:10px 12px; }
          .qx-kpis .kpi-card-value { font-size:20px; margin-top:6px; }
          .qx-kpis .kpi-card-cap { display:none; }
          .qx-kpis .kpi-card-ic { width:28px; height:28px; border-radius:8px; }
          .qx-kpis .kpi-card-ic svg { width:14px; height:14px; }
          .qx-head-name { font-size:14px; }
          .qx-card { grid-template-columns:1fr; height:auto; min-height:0; }
          .qx-list { max-height:65vh; border-right:none; }
          .qx-card.has-sel .qx-list { display:none; }
          .qx-card:not(.has-sel) .qx-list { max-height:none; }
          .qx-card:not(.has-sel) .qx-panel { display:none; }   /* phones: list OR thread, never a blank pane under the list */
          .qx-panel { min-height:70vh; }
          .qx-back { display:inline-flex; }
          .qx-search { width:100%; }
          .qx-chips { width:100%; overflow-x:auto; flex-wrap:nowrap; -webkit-overflow-scrolling:touch; padding-bottom:2px; }
          .qx-chips .chip { flex:none; }
          .qx-side { width:100vw; border-left:none; }
          .kpi-grid.qx-kpis { grid-template-columns:repeat(2, minmax(0,1fr)) !important; gap:8px; }
          .qx-bubble { max-width:88%; }
          .qx-head-btns .btn-txt { display:none; }
        }`;
  }

  private composeNewHtml(): string {
    const opts = (this.contacts || []).map(ct => `<option value="${this.esc(`${ct.name} — ${ct.phone}`)}"></option>`).join('');
    const lineOpts = this.lines
      ? (this.lines.map(l => `<option value="${this.esc(l.id)}" ${l.id === this.composeLine ? 'selected' : ''}>${this.esc(l.name || l.number)}</option>`).join('') || '<option value="">No lines</option>')
      : '<option value="">Loading lines…</option>';
    return `
      <div class="qx-newtext">
        <div class="qx-newtext-row">
          <span style="font-size:13px; font-weight:700;">✉️ New text</span>
          <input id="qx-nt-to" list="qx-nt-contacts" placeholder="${this.contacts ? 'Cleaner, vendor, owner — or a phone number' : 'Loading contacts…'}" value="${this.esc(this.composeTo)}" style="flex:2; min-width:200px;" autocomplete="off">
          <datalist id="qx-nt-contacts">${opts}</datalist>
          <select id="qx-nt-line" title="Send from this Quo line" style="flex:1; min-width:130px;">${lineOpts}</select>
        </div>
        <textarea id="qx-nt-msg" rows="2" maxlength="${SMS_MAX}" placeholder="Type the message…">${this.esc(this.composeMsg)}</textarea>
        <div class="qx-compose-row">
          ${this.composeNote ? `<span class="qx-note err">${this.esc(this.composeNote)}</span>` : ''}
          <button type="button" class="btn btn-secondary" data-qx-closecompose style="margin-left:auto;">Cancel</button>
          <button type="button" class="btn btn-primary" data-qx-sendnew ${this.composeSending ? 'disabled' : ''}>${this.composeSending ? 'Sending…' : 'Send ▸'}</button>
        </div>
      </div>`;
  }

  private actionsStrip(): string {
    if (!this.actions.length) return '';
    const rows = this.actions.slice(0, 12).map(a => `
      <div class="qx-act ${a.source !== 'manual' ? 'auto' : ''}">
        <button type="button" class="qx-act-check" data-qx-actdone="${this.esc(a.id)}" title="Mark handled">☐</button>
        <span class="qx-act-note" ${a.conversation_id || a.phone ? `data-qx-actopen="${this.esc(a.conversation_id || '')}" data-qx-actid="${this.esc(a.id)}" style="cursor:pointer;"` : ''}>${this.esc(a.note)}</span>
        <span class="qx-act-meta">${a.source !== 'manual' ? '🤖 auto' : this.esc((a.created_by || '').split('@')[0])} · ${this.esc(this.fmtT(a.created_at))}</span>
      </div>`).join('');
    return `
      <div class="qx-actions">
        <div class="qx-actions-head">⚡ Action items <span class="qx-nr" style="margin-left:0;">${this.actions.length} open</span></div>
        ${rows}
        ${this.actions.length > 12 ? `<div class="qx-act-meta" style="padding:4px 12px;">+${this.actions.length - 12} more…</div>` : ''}
      </div>`;
  }

  // ── the thread panel ───────────────────────────────────────────────────
  private bubble(m: any): string {
    if (m.kind === 'call') {
      const label = m.missed ? '📞 Missed call' : `📞 ${m.direction === 'incoming' ? 'Incoming' : 'Outgoing'} call${m.duration ? ` · ${Math.max(1, Math.round(m.duration / 60))}m` : ''}`;
      return `<div class="qx-call ${m.missed ? 'missed' : ''}">${label} · ${this.esc(this.fmtT(m.at))}</div>`;
    }
    const media: any[] = Array.isArray(m.media) ? m.media : (Array.isArray(m.images) ? m.images.map((u: string) => ({ url: u, type: 'image/' })) : []);
    const isImg = (x: any) => /^image\//i.test(String(x.type || '')) || /\.(jpe?g|png|gif|webp|heic)(\?|$)/i.test(String(x.url || ''));
    // R116 #2: a group message names its sender (vendor / teammate / the number) — never "Guest"; a 1:1 thread
    // with a vendor / cleaner / teammate names them too (the server resolves m.sender from the rosters).
    const grp = this.isGroup(this.selected);
    const sender = m.direction === 'incoming' && m.sender ? String(m.sender.name || '') : '';
    const who = m.direction === 'incoming' ? this.esc(sender || (grp ? this.fmtPhoneUS(m.from) || 'Unknown number' : 'Guest')) : 'Us';
    const via = m.channel === 'pms' && m.communication_type ? ` · ${this.esc(m.communication_type)}` : '';
    const tag = grp && m.direction === 'incoming' ? `<div class="qx-bubble-who">${who}${m.sender && QuoInboxPage.MEMBER_KIND[String(m.sender.kind || '')] ? ` <span class="qx-who-kind">${this.esc(QuoInboxPage.MEMBER_KIND[String(m.sender.kind)])}</span>` : ''}</div>` : '';
    return `
          <div class="qx-bubble ${m.direction === 'incoming' ? 'in' : 'out'}">${tag}${m.body ? this.esc(m.body) : ''}${media.map((x: any) => isImg(x)
            ? `<a href="${this.esc(x.url)}" target="_blank" rel="noopener"><img src="${this.esc(x.url)}" alt="photo" style="max-width:220px; max-height:220px; border-radius:8px; display:block; margin-top:4px;"></a>`
            : `<a href="${this.esc(x.url)}" target="_blank" rel="noopener">📎 attachment</a>`).join('')}<div class="qx-bubble-t">${tag ? '' : `${who} · `}${this.esc(this.fmtT(m.at))}${via}</div></div>`;
  }
  private threadPanel(): string {
    const r = this.selected;
    const chans: string[] = r.channels || [];
    const tabBtn = (t: Tab, label: string, dot = false) => `<button type="button" class="seg-tab ${this.tab === t ? 'active' : ''}" data-qx-tab="${t}" role="tab">${label}${dot ? ' <span class="qx-tab-dot" title="Waiting on a reply here"></span>' : ''}</button>`;
    const tabs = [
      chans.includes('pms') ? tabBtn('pms', this.pmsLabel(r) === 'PMS' ? 'PMS' : `PMS · ${this.esc(this.pmsLabel(r))}`, !!(r.pms && r.pms.needs_reply)) : '',
      chans.includes('sms') ? tabBtn('sms', 'SMS', !!(r.sms && r.sms.needs_reply)) : '',
      r.ticket_id ? tabBtn('issue', 'Issue') : '',
      tabBtn('notes', `Notes${this.notes && this.notes.length ? ` <span class="qx-n">${this.notes.length}</span>` : ''}`),
    ].join('');
    const urgent = r.severity === 'high' || r.severity === 'emergency' ? `<span class="pill pill-red">Urgent</span>` : '';
    const sub = (this.isGroup(r)
      ? [`Group text${r.sms && r.sms.line ? ` via ${r.sms.line}` : ''}`]
      : [r.guest && r.guest.phone, r.sms && r.sms.line ? `via ${r.sms.line}` : '', r.property && r.property.name]).filter(Boolean).map(x => this.esc(x)).join(' · ');
    const stay = r.reservation && (r.reservation.phase || r.reservation.check_in) ? `
      <div class="qx-guest">${this.phasePill(r)}${r.property && r.property.name ? `<span>🏠 ${this.esc(r.property.name)}</span>` : ''}${r.reservation.check_in ? `<span>📅 ${this.esc(this.fmtD(r.reservation.check_in))} → ${this.esc(this.fmtD(r.reservation.check_out))}</span>` : ''}</div>` : '';
    let body = '';
    if (this.tab === 'sms' || this.tab === 'pms') {
      const t = this.threadData[this.tab];
      const msgs: any[] = t && Array.isArray(t.messages) ? t.messages : [];
      const inner = this.threadBusy[this.tab]
        ? `<div style="padding:30px; text-align:center;">${LoadingSpinner.render()}</div>`
        : this.threadErr[this.tab]
          ? `<div class="qx-empty">⚠️ Couldn't load this ${this.tab === 'pms' ? 'PMS' : 'SMS'} thread — <button type="button" class="btn btn-secondary btn-sm" data-qx-retrythread="${this.tab}">Retry</button></div>`
          : (msgs.map(m => this.bubble(m)).join('') || `<div class="qx-empty">No messages ${this.tab === 'sms' ? 'or calls ' : ''}in this thread yet.</div>`);
      body = `<div class="qx-thread" id="qx-thread"><div class="qx-pt">times in PT</div>${inner}</div>${this.composeHtml()}`;
    } else if (this.tab === 'issue') {
      const tk = this.pmsTicket(r);
      body = `<div class="qx-sec">${this.sentimentCard(r, tk, true)}</div>`;
    } else {
      const canNote = this.noteKeys(r).length > 0;
      body = `<div class="qx-sec">
        <div class="qx-box"><div class="h">Internal notes</div><div style="font-size:12px; color:var(--text-muted);">Notes stay inside the dash — the guest never sees them. They follow the conversation, so the same guest shows them on the All, PMS and SMS views.</div></div>
        ${canNote ? `<textarea class="input" id="qx-notedraft" rows="2" maxlength="1000" placeholder="Add a note about this guest or stay…">${this.esc(this.noteDraft)}</textarea>
        <div style="display:flex; justify-content:flex-end;"><button type="button" class="btn btn-primary btn-sm" data-qx-addnote ${this.noteSaving ? 'disabled' : ''}>${this.noteSaving ? 'Saving…' : 'Save note'}</button></div>`
        : `<div class="qx-empty">This thread isn't in the inbox yet, so there's no conversation to attach a note to — once it shows in the list, notes work here.</div>`}
        <div class="qx-notes-list">${this.notes === null ? `<div style="padding:16px; text-align:center;">${LoadingSpinner.render()}</div>` : (this.notes.map(n => `<div class="qx-noteitem">${this.esc(n.text)}<div class="m">${this.esc((n.by || '').split('@')[0])} · ${this.esc(this.fmtT(n.at))}</div></div>`).join('') || (canNote ? `<div class="qx-empty">No notes yet.</div>` : ''))}</div>
      </div>`;
    }
    return `
      <div class="qx-panel-head">
        <button type="button" class="btn btn-secondary btn-sm qx-back" data-qx-back title="Back to the inbox">←</button>
        <div class="qx-av lg ${this.avClass(r)}">${this.esc(this.initials(r))}</div>
        <div class="qx-head-txt">
          <div class="qx-head-name">${this.isGroup(r) ? `<span class="qx-grp-name" title="${this.esc(this.label(r))}">${this.esc(this.label(r))}</span>` : this.esc(this.label(r))}${urgent}${this.kindPill(r)}${r.sentiment === 'negative' ? '<span class="pill pill-red">Negative</span>' : ''}</div>
          <div class="qx-head-sub">${sub || '&nbsp;'}</div>
        </div>
        <div class="qx-head-btns">
          <button type="button" class="btn btn-secondary btn-sm qx-details-btn" data-qx-details>Details</button>
          ${r.sms && r.sms.conversation_id ? `<button type="button" class="btn btn-secondary btn-sm" data-qx-done title="Mark the SMS thread done without replying">✓ <span class="btn-txt">Mark done</span></button>` : ''}
        </div>
      </div>
      <div class="qx-tabs"><div class="seg-tabs" role="tablist">${tabs}</div></div>
      ${stay}
      ${body}`;
  }
  private composeHtml(): string {
    const r = this.selected;
    const pms = this.tab === 'pms';
    const can = pms ? !!(r.pms && r.pms.conversation_id) : !!(r.sms && r.sms.phone_number_id);
    if (!can) return '';
    const chLabel = pms
      ? `Replying via <b>PMS</b> · ${this.esc(this.pmsLabel(r))} thread (through Hostaway) — the guest sees it in the booking channel`
      : this.isGroup(r)
        ? `Replying to the <b>group</b> (${this.esc((this.membersOf(r).length || (r.sms.participants || []).length))} people) via <b>SMS</b>${r.sms.line ? ` · ${this.esc(r.sms.line)}` : ''} — everyone on it sees your reply`
        : `Replying via <b>SMS</b>${r.sms.line ? ` · ${this.esc(r.sms.line)}` : ''} → ${this.esc((r.sms.participants || []).join(', '))}`;
    const off = pms && !this.pmsSend;
    return `
      <div class="qx-compose">
        <div class="qx-compose-ch">${chLabel}</div>
        ${off ? `<div class="qx-off" style="margin-bottom:8px;">PMS replies are switched off on this server (MESSAGING_PMS_SEND). Read here; reply by SMS or in Hostaway until the flag is on.</div>` : ''}
        <textarea id="qx-draft" rows="2" maxlength="${pms ? PMS_MAX : SMS_MAX}" placeholder="${pms ? 'Type a reply for the PMS thread…' : 'Type a reply…'}" ${off ? 'disabled' : ''}>${this.esc(this.draft)}</textarea>
        <div class="qx-compose-row">
          ${pms ? '' : `<label style="font-size:12px; color:var(--text-muted); display:flex; align-items:center; gap:5px; cursor:pointer;"><input type="checkbox" id="qx-markdone" ${this.markDone ? 'checked' : ''}> mark done after sending</label>`}
          ${this.note ? `<span class="qx-note ${this.note.startsWith('Send failed') ? 'err' : ''}">${this.esc(this.note)}</span>` : ''}
          <button type="button" class="btn btn-primary" data-qx-send ${this.sending || off ? 'disabled' : ''} style="margin-left:auto;">${this.sending ? 'Sending…' : (pms ? 'Review & send ▸' : 'Send ▸')}</button>
        </div>
      </div>`;
  }
  // The thread payload's ticket is used only when it is THIS row's PMS conversation (a stale or mismatched payload must never dress another guest with someone else's sentiment).
  private pmsTicket(r: any): any {
    const t = this.threadData.pms;
    return t && t.ticket && r && r.pms && String(t.conversation_id || '') === String(r.pms.conversation_id || '') ? t.ticket : null;
  }
  private sentimentCard(r: any, tk: any, full = false): string {
    const sent = (tk && tk.sentiment) || r.sentiment;
    const summary = (tk && tk.summary) || r.summary || '';
    if (!sent && !summary && !r.ticket_id) return '';
    const pill = sent === 'negative' ? 'pill-red' : sent === 'positive' ? 'pill-green' : 'pill-gray';
    const tid = (tk && tk.id) || r.ticket_id;
    const issues: any[] = tk && Array.isArray(tk.issues) ? tk.issues : [];
    return `
      <div class="qx-sc qx-sent-card ${sent === 'positive' ? 'pos' : ''}">
        <div class="h">Sentiment <span class="pill ${pill}" style="margin-left:6px;">${this.esc(sent || 'unknown')}</span>${r.severity ? ` <span class="pill ${r.severity === 'high' || r.severity === 'emergency' ? 'pill-red' : r.severity === 'med' ? 'pill-amber' : 'pill-gray'}">${this.esc(String(r.severity).toUpperCase())}</span>` : ''}</div>
        <div class="qx-sum">${this.esc(summary || 'Flagged by the hourly message scan.')}</div>
        ${full && tk && tk.english ? `<div class="qx-eng">🌐 ${this.esc(tk.english)}</div>` : ''}
        ${full && tk && tk.excerpt ? `<div class="qx-eng"><i>“${this.esc(tk.excerpt)}”</i></div>` : ''}
        ${issues.length ? `<ul class="qx-issues">${issues.map(i => `<li>${this.esc(i.title)}${i.severity ? ` <span class="qx-n">· ${this.esc(i.severity)}</span>` : ''}</li>`).join('')}</ul>` : ''}
        ${r.recovery_status ? `<div class="qx-kv" style="margin-top:6px;"><span>Guest Recovery</span><span>${this.esc(stateLabel(r))}</span></div>` : ''}
        ${tid ? `<button type="button" class="btn btn-secondary btn-sm" data-qx-issue="${this.esc(tid)}">Open issue</button>` : ''}
        ${tid && (r.recovery_status || sent === 'negative') ? `<button type="button" class="btn btn-secondary btn-sm" data-qx-recovery="${this.esc(tid)}">Guest Recovery</button>` : ''}
      </div>`;
  }
  private sideHtml(): string {
    const r = this.selected;
    const head = `<div class="qx-side-head">Reservation details<button type="button" class="qx-side-close" data-qx-sideclose aria-label="Close">✕</button></div>`;
    if (!r) return head + `<div class="qx-empty">Open a conversation to see the stay, the contact and the sentiment here.</div>`;
    const rez = r.reservation;
    const tk = this.pmsTicket(r);
    const nights = rez && rez.nights != null ? rez.nights : (rez && rez.check_in && rez.check_out ? Math.round((Date.parse(rez.check_out) - Date.parse(rez.check_in)) / 86400000) : null);
    const stayCard = `
      <div class="qx-sc">
        <div style="display:flex; align-items:flex-start; gap:8px; justify-content:space-between;">
          <div class="qx-prop-name">${this.esc((r.property && r.property.name) || (rez ? 'Reservation' : 'No stay matched'))}</div>${this.phasePill(r)}
        </div>
        ${rez && rez.check_in ? `
        <div class="qx-dl3">
          <div><div class="qx-dl">Check-in</div><div class="qx-dv">${this.esc(this.fmtD(rez.check_in))}</div></div>
          <div><div class="qx-dl">Check-out</div><div class="qx-dv">${this.esc(this.fmtD(rez.check_out))}</div></div>
          <div><div class="qx-dl">Nights</div><div class="qx-dv">${nights != null ? this.esc(nights) : '—'}</div></div>
        </div>` : `<div style="font-size:12px; color:var(--text-muted); margin-top:6px;">${r.sms ? 'No reservation matched this number in the last year.' : 'No reservation on this conversation.'}</div>`}
        ${rez ? `<div style="margin-top:8px;">
          ${rez.channel_name ? `<div class="qx-kv"><span>Channel</span><span>${this.esc(this.pmsLabel(r))}</span></div>` : ''}
          ${rez.status ? `<div class="qx-kv"><span>Status</span><span>${this.esc(rez.status)}</span></div>` : ''}
          ${rez.guests != null ? `<div class="qx-kv"><span>Guests</span><span>${this.esc(rez.guests)}</span></div>` : ''}
          ${rez.confirmation_code ? `<div class="qx-kv"><span>Confirmation</span><span>${this.esc(rez.confirmation_code)}</span></div>` : ''}
        </div>` : ''}
        ${rez && rez.hostaway_url ? `<a class="btn btn-secondary btn-sm" href="${this.esc(rez.hostaway_url)}" target="_blank" rel="noopener">Open in Hostaway ↗</a>` : ''}
      </div>`;
    const phone = r.guest && r.guest.phone, email = r.guest && r.guest.email;
    const contactCard = `
      <div class="qx-sc">
        <div class="h">Contact</div>
        ${phone ? `<div class="qx-contact">📞 <span class="v">${this.esc(phone)}</span><button type="button" class="qx-copy" data-qx-copy="${this.esc(phone)}">copy</button></div>` : ''}
        ${email ? `<div class="qx-contact">✉️ <span class="v" title="${this.esc(email)}">${this.esc(email)}</span><button type="button" class="qx-copy" data-qx-copy="${this.esc(email)}">copy</button></div>` : ''}
        ${!phone && !email ? `<div style="font-size:12px; color:var(--text-muted);">No phone or email on file${r.pms ? ' — Airbnb masks most guest contacts' : ''}.</div>` : ''}
        ${(r.channels || []).length ? `<div class="qx-kv" style="margin-top:6px;"><span>Channels</span><span>${(r.channels || []).map((c: string) => c === 'sms' ? `SMS${r.sms && r.sms.line ? ` (${this.esc(r.sms.line)})` : ''}` : this.esc(this.pmsLabel(r))).join(' + ')}</span></div>` : ''}
        ${r.guest && QuoInboxPage.KINDS[String(r.guest.contact_kind || '')] ? `<div class="qx-kv"><span>Roster</span><span>${this.esc(r.guest.contact_kind)}</span></div>` : ''}
      </div>`;
    const acts = this.actionsFor(r);
    const actionsCard = `
      <div class="qx-sc">
        <div class="h">Action items</div>
        ${acts.length ? acts.slice(0, 6).map(a => `<div class="qx-act-mini"><button type="button" class="qx-act-check" data-qx-actdone="${this.esc(a.id)}" title="Mark handled">☐</button><span style="flex:1;">${this.esc(a.note)}<div class="qx-act-meta">${a.source !== 'manual' ? '🤖 auto' : this.esc((a.created_by || '').split('@')[0])} · ${this.esc(this.fmtT(a.created_at))}</div></span></div>`).join('') : `<div style="font-size:12px; color:var(--text-muted);">Nothing open for this ${this.isGroup(r) ? 'group' : 'guest'}.</div>`}
        <button type="button" class="btn btn-secondary btn-sm" data-qx-addaction>＋ Action item</button>
      </div>`;
    const sid = r.sms && r.sms.conversation_id;
    // R116 #2: a GROUP thread has no stay and no single contact — its members are the card
    const groupCard = this.isGroup(r) ? (() => {
      const members = this.membersOf(r);
      const rows = members.map((m: any) => `
        <div class="qx-member">
          <span class="qx-av sm av-grp-m">${String(m.name || '').trim() ? this.esc(this.initials({ guest: { name: m.name } })) : '#'}</span>
          <span class="qx-mtxt"><span class="qx-mname">${this.esc(this.memberName(m))}</span><span class="qx-msub">${String(m.name || '').trim() ? `${QuoInboxPage.MEMBER_KIND[String(m.kind || '')] ? `${this.esc(QuoInboxPage.MEMBER_KIND[String(m.kind)])} · ` : ''}${this.esc(this.fmtPhoneUS(m.phone))}` : 'not on a roster — a number typed on the work order'}</span></span>
        </div>`).join('');
      return `
      <div class="qx-sc">
        <div class="h">Members <span class="qx-n">${members.length}</span></div>
        ${rows || `<div style="font-size:12px; color:var(--text-muted);">${this.esc((r.sms && (r.sms.participants || []).join(', ')) || '')}</div>`}
        <div style="font-size:12px; color:var(--text-muted); margin-top:8px; line-height:1.45;">One Quo group text — every member sees every message and each other's number. Replies inside a vendor group also land on its work order.</div>
      </div>`;
    })() : '';
    const triageCard = sid ? `
      <div class="qx-sc">
        <div class="h">SMS triage</div>
        ${r.dismissed
          ? `<div style="font-size:12px; color:var(--text-muted);">Dismissed · ${this.esc(this.outcomeLabel(r.dismiss_outcome))} · ${this.esc((r.dismiss_by || '').split('@')[0])}</div><button type="button" class="btn btn-secondary btn-sm" data-qx-restore="${this.esc(sid)}">↩ Restore</button>`
          : `<div style="font-size:12px; color:var(--text-muted);">Not a guest, handled by phone, nothing to do? Dismiss with an outcome — new activity resurfaces it.</div><button type="button" class="btn btn-secondary btn-sm" data-qx-dismiss="${this.esc(sid)}">✕ Dismiss with outcome</button>`}
      </div>` : '';
    if (groupCard) return head.replace('Reservation details', 'Group details') + `<div class="qx-side-body">${groupCard}${actionsCard}${triageCard}</div>`;
    return head + `<div class="qx-side-body">${stayCard}${contactCard}${this.sentimentCard(r, tk)}${actionsCard}${triageCard}</div>`;
  }
  // R112 review [15]: the confirm overlay lives on document.body (like the
  // diag modal) — opening it, cancelling it and its "Sending…" state never
  // redraw #qx-body, so the thread keeps its scroll position and the draft
  // its caret (draw() rebuilt #qx-thread at scrollTop 0 behind the modal,
  // and Cancel left the thread on its oldest message). It closes itself when
  // the page body leaves the DOM — navigation re-renders the shared content
  // container, which the observer sees; the poll timers are the backstop.
  // R115 guest: the modal itself is shared/components/PmsSendConfirm.
  private openConfirm(): void {
    this.closeConfirm();
    const r = this.selected;
    if (!r) return;
    this.confirm.open({
      channel: this.pmsLabel(r), guest: this.label(r), property: (r.property && r.property.name) || '—',
      stay: r.reservation && r.reservation.check_in ? `${this.fmtD(r.reservation.check_in)} → ${this.fmtD(r.reservation.check_out)}` : undefined,
      body: this.draft,
    }, { onSend: () => this.confirmPmsSend(), watch: this.container, isAlive: () => !!(this.bodyEl && this.bodyEl.isConnected), zIndex: 9500 });
  }
  private closeConfirm(): void { this.confirm.close(); }
  private setConfirmBusy(busy: boolean): void { this.confirm.setBusy(busy); }

  // ── bindings ───────────────────────────────────────────────────────────
  private bindRows(): void {
    const c = this.container;
    c.querySelectorAll<HTMLElement>('[data-qx-open]').forEach(el => {
      const open = () => { const row = this.rows.find(x => x.id === el.dataset.qxOpen); if (row) void this.openThread(row); };
      el.addEventListener('click', open);
      el.addEventListener('keydown', (e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } });
    });
    c.querySelectorAll<HTMLElement>('#qx-list [data-qx-dismiss]').forEach(el => {
      const start = (e: Event) => { e.stopPropagation(); e.preventDefault(); this.dismissing = el.dataset.qxDismiss!; this.patchList(); };
      el.addEventListener('click', start);
      el.addEventListener('keydown', (e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') start(e); });
    });
    c.querySelector('[data-qx-canceldismiss]')?.addEventListener('click', () => { this.dismissing = null; this.patchList(); });
    c.querySelectorAll<HTMLElement>('[data-qx-outcome]').forEach(el => el.addEventListener('click', () => void this.dismissConv(el.dataset.qxConv!, el.dataset.qxOutcome!)));
    c.querySelectorAll<HTMLElement>('#qx-list [data-qx-restore]').forEach(el => el.addEventListener('click', (e) => { e.stopPropagation(); void this.restoreConv(el.dataset.qxRestore!); }));
    c.querySelectorAll<HTMLElement>('[data-qx-line]').forEach(b => b.addEventListener('click', () => {
      this.setLineFilter(b.dataset.qxLine || '');
      const hasRows = !this.lineFilter || this.rows.some(r => r.sms && String(r.sms.phone_number_id || '') === this.lineFilter);
      if (hasRows) { this.patchList(); void this.loadInbox(true); } else void this.loadInbox();
    }));
    c.querySelector('[data-qx-diag]')?.addEventListener('click', () => void this.openDiag());
    c.querySelector('[data-qx-togglequiet]')?.addEventListener('click', () => { this.showQuiet = !this.showQuiet; this.patchList(); });
    c.querySelector('[data-qx-toggledismissed]')?.addEventListener('click', () => { this.showDismissed = !this.showDismissed; this.patchList(); });
  }
  // R105 #1: the Quo diagnostic — every line's counts + where one number's thread sits
  private async openDiag(): Promise<void> {
    const phone = prompt('Phone number to look for (leave blank for the per-line counts only):', '');
    if (phone === null) return;
    const box = document.createElement('div');
    box.className = 'qx-ovl';
    box.innerHTML = `<div class="qx-modal" style="max-height:80vh; overflow:auto; font-size:12.5px;"><div style="font-weight:700; margin-bottom:8px;">🩺 Quo diagnostic</div><pre id="qx-diag-out" style="white-space:pre-wrap; font-family:inherit; margin:0;">Checking every line… (a few seconds)</pre><div style="text-align:right; margin-top:10px;"><button type="button" class="btn btn-secondary btn-sm" data-qx-diag-close>Close</button></div></div>`;
    document.body.appendChild(box);
    box.addEventListener('click', (e) => { if (e.target === box || (e.target as HTMLElement).closest('[data-qx-diag-close]')) box.remove(); });
    const out = box.querySelector<HTMLElement>('#qx-diag-out')!;
    const r = await fetch(`${QUO}/diag${phone.trim() ? `?phone=${encodeURIComponent(phone.trim())}` : ''}`).catch(() => null);
    const j = r ? await r.json().catch(() => null) : null;
    if (!r || !j) { out.textContent = 'The check failed — try again.'; return; }
    if (!r.ok || j.error) { out.textContent = `Check failed — ${j.error || `HTTP ${r.status}`}`; return; }
    if (!j.configured) { out.textContent = 'Quo is NOT configured on this server (no QUO_API_KEY / OPENPHONE_API_KEY / QUO_KEY).'; return; }
    const lines = (j.lines || []).map((l: any) => `${l.is_vendor_line ? '🔧 ' : ''}${l.name || l.number} (${l.number})\n   active ${l.active_count ?? '?'} · all ${l.all_count ?? '?'} · newest ${l.newest_activity_at ? this.fmtT(l.newest_activity_at) : '—'}${l.error ? `\n   ⚠ ${l.error}` : ''}${l.found ? `\n   ✓ ${j.phone} is here — ${l.found.in_active_page ? `#${l.found.rank} of the first ${l.page_limit || 30} active threads (the inbox shows it)` : `active but beyond the first ${l.page_limit || 30}, or marked Done in Quo — pick this line to pull its full list`}; newest ${l.found.newest ? `${l.found.newest.direction} ${this.fmtT(l.found.newest.at)}: ${l.found.newest.body || (l.found.newest.media_n ? `📷 ${l.found.newest.media_n} photos` : '')}` : 'unknown'}` : (j.phone ? `\n   ✕ ${j.phone} not on this line` : '')}`).join('\n');
    out.textContent = `key: ${j.key_env} · webhook secret ${j.webhook_secret_set ? 'set' : 'NOT set'} · excludeInactive ${j.exclude_inactive}\nvendor line expected: ${j.vendor_line_want}\n\n${lines}`;
  }

  private async dismissConv(id: string, outcome: string): Promise<void> {
    const row = this.rows.find(x => x.sms && x.sms.conversation_id === id);
    const r = await fetch(`${QUO}/conversations/${encodeURIComponent(id)}/dismiss`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      // last_activity_at = the watermark of what we SAW — a text landing after this row was fetched must resurface it
      body: JSON.stringify({ outcome, by: this.myEmail(), last_activity_at: (row && row.sms && row.sms.last_activity_at) || (row && row.last_activity_at) || '' }),
    }).catch(() => null);
    if (!r || !r.ok) { alert('Couldn\'t dismiss — try again.'); return; }
    this.dismissing = null;
    if (row) {
      row.dismiss_outcome = outcome; row.dismiss_by = this.myEmail(); row.dismiss_at = new Date().toISOString();
      row.sms.needs_reply = false;
      row.dismissed = !(row.pms && row.pms.needs_reply);
      row.needs_reply = !!(row.pms && row.pms.needs_reply);
    }
    if (this.selected && this.selected.sms && this.selected.sms.conversation_id === id && !(this.selected.pms)) {
      this.selected = null; this.threadData = {}; this.draft = ''; this.markDone = false; this.note = '';
      this.draw();
    } else { this.patchList(); this.patchSide(); }
    await this.loadInbox(true);
  }
  private async restoreConv(id: string): Promise<void> {
    const r = await fetch(`${QUO}/conversations/${encodeURIComponent(id)}/restore`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }).catch(() => null);
    if (!r || !r.ok) { alert('Couldn\'t restore — try again.'); return; }
    const row = this.rows.find(x => x.sms && x.sms.conversation_id === id);
    if (row) { row.dismissed = false; row.dismiss_outcome = null; }
    this.patchList(); this.patchSide();
    await this.loadInbox(true);
  }
  private bindActions(): void {
    const c = this.container;
    c.querySelectorAll<HTMLElement>('#qx-actions-host [data-qx-actdone]').forEach(el => el.addEventListener('click', () => void this.actionDone(el.dataset.qxActdone!)));
    c.querySelectorAll<HTMLElement>('[data-qx-actopen]').forEach(el => el.addEventListener('click', () => {
      const a = this.actions.find(x => x.id === el.dataset.qxActid);
      const row = this.rows.find(x => (x.sms && el.dataset.qxActopen && x.sms.conversation_id === el.dataset.qxActopen)) || (a && a.phone ? this.findRow(this.normPhone(a.phone)) : null);
      if (row) { void this.openThread(row); return; }
      if (a && a.phone_number_id && Array.isArray(a.participants) && a.participants.length) {
        void this.openThread({
          id: `sms:${a.conversation_id || 'action'}`, channels: ['sms'],
          guest: { name: a.guest_name || '', phone: a.phone || a.participants[0] || '', email: null, contact_kind: null },
          property: { listing_id: '', name: a.listing || '' }, reservation: null, last_message: null, last_activity_at: null, needs_reply: false, dismissed: false,
          sms: { conversation_id: a.conversation_id, phone_number_id: a.phone_number_id, participants: a.participants, line: '', display_number: a.phone || (a.participants[0] || ''), needs_reply: false, last_message: null },
          pms: null,
        });
        return;
      }
      alert('This conversation isn\'t in the current inbox and the item predates thread links — find it in Quo by the number: ' + ((a && a.phone) || 'unknown'));
    }));
  }
  private bindSide(): void {
    const c = this.container;
    const side = c.querySelector('#qx-side');
    if (!side) return;
    side.querySelector('[data-qx-sideclose]')?.addEventListener('click', () => { this.detailsOpen = false; side.classList.remove('open'); });
    side.querySelectorAll<HTMLElement>('[data-qx-copy]').forEach(b => b.addEventListener('click', () => {
      const v = b.dataset.qxCopy || '';
      try { void navigator.clipboard.writeText(v).then(() => { b.textContent = 'copied'; setTimeout(() => { b.textContent = 'copy'; }, 1200); }); } catch { /* clipboard unavailable */ }
    }));
    side.querySelectorAll<HTMLElement>('[data-qx-actdone]').forEach(el => el.addEventListener('click', () => void this.actionDone(el.dataset.qxActdone!)));
    side.querySelector('[data-qx-addaction]')?.addEventListener('click', () => void this.addAction());
    side.querySelector<HTMLElement>('[data-qx-dismiss]')?.addEventListener('click', (e) => {
      const el = e.currentTarget as HTMLElement;
      this.dismissing = el.dataset.qxDismiss!; this.showDismissed = this.showDismissed; this.patchList();
      c.querySelector('#qx-list .qx-dismissing')?.scrollIntoView({ block: 'nearest' });
    });
    side.querySelector<HTMLElement>('[data-qx-restore]')?.addEventListener('click', (e) => void this.restoreConv((e.currentTarget as HTMLElement).dataset.qxRestore!));
    this.bindHandoffs(side as HTMLElement);
  }
  // R111 navigation rule: set the focus key FIRST, then app:navigate — never hub_tab for first-class pages
  private bindHandoffs(root: HTMLElement): void {
    root.querySelectorAll<HTMLElement>('[data-qx-issue]').forEach(b => b.addEventListener('click', () => {
      try { sessionStorage.setItem('iss_focus', `msg:${b.dataset.qxIssue}`); } catch { /* private mode */ }
      document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'issues' }));
    }));
    root.querySelectorAll<HTMLElement>('[data-qx-recovery]').forEach(b => b.addEventListener('click', () => {
      try { sessionStorage.setItem('gr_focus', String(b.dataset.qxRecovery || '')); } catch { /* private mode */ }
      document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'guest-recovery' }));
    }));
  }

  // The toolbar's own handlers — bound on a full draw AND on every in-place
  // toolbar patch (patchToolbar), so they always sit on the live elements.
  private bindToolbar(): void {
    const c = this.container;
    c.querySelector('[data-qx-refresh]')?.addEventListener('click', () => this.loadInbox(this.rows.length > 0));
    c.querySelector('.qx-bar2 [data-qx-addaction]')?.addEventListener('click', () => void this.addAction());
    c.querySelector('[data-qx-newtext]')?.addEventListener('click', () => void this.openCompose());
    // search narrows the list in place (the input keeps its caret), seg tabs / window refetch, chips narrow in place
    const qEl = c.querySelector<HTMLInputElement>('#qx-q');
    qEl?.addEventListener('input', () => { this.q = qEl.value; this.patchList(); });
    c.querySelectorAll<HTMLElement>('[data-qx-channel]').forEach(b => b.addEventListener('click', () => {
      const v = (b.dataset.qxChannel as Channel) || 'all';
      if (v === this.channel) return;
      this.channel = v;
      this.patchToolbar(true);   // the active tab + the PMS window control follow the channel
      this.patchList(); void this.loadInbox(true);
    }));
    c.querySelectorAll<HTMLElement>('[data-qx-chip]').forEach(b => b.addEventListener('click', () => {
      const v = (b.dataset.qxChip as any) || '';
      this.chip = this.chip === v ? '' : v;
      c.querySelectorAll<HTMLElement>('[data-qx-chip]').forEach(x => x.classList.toggle('active', x.dataset.qxChip === this.chip));
      this.patchList();
    }));
    const win = c.querySelector<HTMLSelectElement>('#qx-window');
    win?.addEventListener('change', () => { this.window = Number(win.value) || 14; void this.loadInbox(true); });
  }

  private bind(): void {
    const c = this.container;
    c.querySelector('[data-qx-retry]')?.addEventListener('click', () => this.loadInbox());
    c.querySelector('[data-qx-closecompose]')?.addEventListener('click', () => this.closeCompose());
    const ntTo = c.querySelector<HTMLInputElement>('#qx-nt-to');
    ntTo?.addEventListener('input', () => { this.composeTo = ntTo.value; });
    const ntLine = c.querySelector<HTMLSelectElement>('#qx-nt-line');
    ntLine?.addEventListener('change', () => { this.composeLine = ntLine.value; });
    const ntMsg = c.querySelector<HTMLTextAreaElement>('#qx-nt-msg');
    ntMsg?.addEventListener('input', () => { this.composeMsg = ntMsg.value; });
    c.querySelector('[data-qx-sendnew]')?.addEventListener('click', () => void this.sendNew());
    this.bindToolbar();
    this.bindActions();
    this.bindRows();
    this.bindSide();
    // thread panel
    c.querySelectorAll<HTMLElement>('[data-qx-tab]').forEach(b => b.addEventListener('click', () => this.switchTab((b.dataset.qxTab as Tab) || 'sms')));
    c.querySelectorAll<HTMLElement>('[data-qx-retrythread]').forEach(b => b.addEventListener('click', () => void this.retryThread((b.dataset.qxRetrythread as 'sms' | 'pms') || 'sms')));
    c.querySelector('[data-qx-back]')?.addEventListener('click', () => { this.selected = null; this.threadData = {}; this.draft = ''; this.note = ''; this.closeConfirm(); this.draw(); });
    c.querySelector('[data-qx-details]')?.addEventListener('click', () => { this.detailsOpen = !this.detailsOpen; c.querySelector('#qx-side')?.classList.toggle('open', this.detailsOpen); });
    const ta = c.querySelector<HTMLTextAreaElement>('#qx-draft');
    ta?.addEventListener('input', () => { this.draft = ta.value; });   // mirror so redraws/polls don't wipe it
    const mk = c.querySelector<HTMLInputElement>('#qx-markdone');
    mk?.addEventListener('change', () => { this.markDone = mk.checked; });
    c.querySelector('[data-qx-send]')?.addEventListener('click', () => void this.send());
    c.querySelector('[data-qx-done]')?.addEventListener('click', () => void this.doneWithout());
    const nd = c.querySelector<HTMLTextAreaElement>('#qx-notedraft');
    nd?.addEventListener('input', () => { this.noteDraft = nd.value; });
    c.querySelector('[data-qx-addnote]')?.addEventListener('click', () => void this.addNote());
    const panel = c.querySelector<HTMLElement>('#qx-panel');
    if (panel) this.bindHandoffs(panel);
    // (the PMS confirm modal binds itself in openConfirm — it lives on document.body)
    // Jump to the newest message only right after a real open/send — never on a routine redraw
    const th = c.querySelector('#qx-thread');
    if (th && this.pendingScroll) { th.scrollTop = th.scrollHeight; this.pendingScroll = false; }
  }
}
